import React from "react";
import { QuestionCircle, ChevronDown, ChevronUp } from "react-bootstrap-icons";
import { Button, Form, Collapse } from "react-bootstrap";
import boardData from "../data/boardData.json";
import tileData, {
  WORMHOLES,
  EXPANSIONS,
  ANOMALIES,
  PLANET_TRAITS,
} from "../data/tileData";
import factionData from "../data/factionData";
import adjacencyData from "../data/adjacencyData.json";
import { getAdjacentPositions } from "../helpers/Adjacency";
import {
  getSliceImbalance,
  getSliceMap,
  getSliceStats,
} from "../helpers/Slices";
import HelpModal from "./HelpModal";
import SetPlayerNameModal from "./SetPlayerNameModal";
import SetFactionsModal from "./SetFactionsModal";

/**
 * How many swaps balanceSlices will make before giving up. It stops as soon as no swap improves
 * things, which on the built-in boards happens long before this, so it only guards against a
 * pathological board grinding the browser.
 */
const BALANCE_SLICES_MAX_PASSES = 200;

/** Ignore improvements this small, so floating point noise can't keep the swap loop going. */
const BALANCE_EPSILON = 1e-9;

const expansionCheck = (includedExpansions) => (id) =>
  (!tileData.pok.includes(id) || includedExpansions[EXPANSIONS.POK]) &&
  (!tileData.uncharted.includes(id) || includedExpansions[EXPANSIONS.UnS]) &&
  (!tileData.sun.includes(id) || includedExpansions[EXPANSIONS.AS]) &&
  (!tileData.asyncLanes.includes(id) || includedExpansions[EXPANSIONS.Async]) &&
  (!tileData.te.includes(id) || includedExpansions[EXPANSIONS.TE]);

const pokFreeBoardStyles = (playerCount) =>
  Object.keys(boardData.styles[playerCount]).filter(
    (style) => !boardData.styles[playerCount][style].requiresPok,
  );

const IP_POLICY_LINK = `<a href="https://images-cdn.fantasyflightgames.com/filer_public/fa/b1/fab15a15-94a6-404c-ab86-6a3b0e77a7a0/ip_policy_031419_final_v21.pdf" target="_blank" rel="noreferrer">Asmodee guidelines on community usage of TI4 intellectual property</a>`;

/**
 * A link to the reference document for a set of fan-made content, shown at the bottom of its help modal
 */
const referenceLink = (url) =>
  `<p class="mb-0"><a href="${url}" target="_blank" rel="noreferrer">Reference document for this content</a></p>`;

class MapOptions extends React.Component {
  constructor(props) {
    super(props);
    const startingValues = {
      numberOfPlayers: [2, 3, 4, 5, 6],
      pokNumberOfPlayers: [7, 8],
      // Styles flagged "requiresPok" lean on tiles that only exist in Prophecy
      // of Kings (the big four ring boards can't be filled from the base pool),
      // so they're only offered once that expansion is on. Keep new styles
      // appended to the end of their player count in boardData.json: shared
      // links encode the board style as its index in these lists.
      boardStyles: {
        2: pokFreeBoardStyles("2"),
        3: pokFreeBoardStyles("3"),
        4: pokFreeBoardStyles("4"),
        5: pokFreeBoardStyles("5"),
        6: pokFreeBoardStyles("6"),
      },
      boardStylesPok: {
        2: Object.keys(boardData.styles["2"]).map((key) => key),
        3: Object.keys(boardData.styles["3"]).map((key) => key),
        4: Object.keys(boardData.styles["4"]).map((key) => key),
        5: Object.keys(boardData.styles["5"]).map((key) => key),
        6: Object.keys(boardData.styles["6"]).map((key) => key),
        7: Object.keys(boardData.styles["7"]).map((key) => key),
        8: Object.keys(boardData.styles["8"]).map((key) => key),
      },
      pickStyles: ["balanced", "random", "resource", "influence", "custom"],
      placementStyles: ["slice", "initial", "home", "random"],
      factions: [...factionData["factions"]],
      pokFactions: [...factionData["pokFactions"]],
      teFactions: [...factionData["teFactions"]],
      dsFactions: [...factionData["dsFactions"]],
      homeworlds: factionData["homeSystems"],
      pokHomeworlds: factionData["pokHomeSystems"],
      teHomeworlds: factionData["teHomeSystems"],
      dsHomeworlds: factionData["dsHomeSystems"],
    };
    const startingPlayers = 6;

    this.state = {
      optionsPossible: startingValues,
      currentNumberOfPlayers: startingPlayers,
      currentNumberOfPlayersOptions: startingValues["numberOfPlayers"],
      currentBoardStyleOptions: startingValues["boardStyles"][startingPlayers],
      currentBoardStyle: startingValues["boardStyles"][startingPlayers][0],
      currentPickStyle: startingValues["pickStyles"][0],
      currentPlacementStyle: startingValues["placementStyles"][0],
      currentSeed: "",
      userSetSeed: false,
      fanContent: false,
      pickFactions: false,
      pickMultipleFactions: false,
      shuffleBoards: false,
      reversePlacementOrder: false,
      forceWormholes: false,
      forceAnomalies: false,
      ensureFactionAnomalies: true,
      balancePlanetTraits: false,
      balanceSlices: true,
      generated: false,
      advancedSettingsOpen: false,

      fanContentHelp: false,
      unchartedSpaceHelp: false,
      discordantStarsHelp: false,
      ascendentSunHelp: false,
      pickFactionsHelp: false,
      boardStyleHelp: false,
      pickStyleHelp: false,
      placementStyleHelp: false,
      setPlayerNamesHelp: false,
      setFactionsHelp: false,
      pickMultipleFactionsHelp: false,
      shufflePriorityHelp: false,
      reversePlacementOrderHelp: false,
      forceWormholesHelp: false,
      forceAnomaliesHelp: false,
      ensureFactionAnomaliesHelp: false,
      balancePlanetTraitsHelp: false,

      resourceWeight: 70,
      influenceWeight: 30,
      planetCountWeight: 15,
      specialtyWeight: 50,
      anomalyWeight: 10,
      wormholeWeight: 25,

      shuffleThreshold: 5,
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleFactionsChange = this.handleFactionsChange.bind(this);
    this.updatePok = this.updatePok.bind(this);
    this.updateUncharted = this.updateUncharted.bind(this);
    this.updateDS = this.updateDS.bind(this);
    this.updateSun = this.updateSun.bind(this);
    this.updateFanHyp = this.updateFanHyp.bind(this);
    this.updatePlayerCount = this.updatePlayerCount.bind(this);
    this.updateBoardStyle = this.updateBoardStyle.bind(this);
    this.updateSeed = this.updateSeed.bind(this);

    this.ensureAnomalies = this.ensureAnomalies.bind(this);
    this.ensureWormholesForType = this.ensureWormholesForType.bind(this);
    this.ensurePlanetTraitBalance = this.ensurePlanetTraitBalance.bind(this);
    this.balanceSlices = this.balanceSlices.bind(this);

    this.updateBoardStyleOptions = this.updateBoardStyleOptions.bind(this); // TODO is the bind needed?

    this.encodeSettings = this.encodeSettings.bind(this);
    this.decodeSettings = this.decodeSettings.bind(this);

    this.generateBoard = this.generateBoard.bind(this);
    this.getNewTileSet = this.getNewTileSet.bind(this);

    this.toggleFanContentHelp = this.toggleFanContentHelp.bind(this);
    this.toggleUnchartedSpaceHelp = this.toggleUnchartedSpaceHelp.bind(this);
    this.toggleDiscordantStarsHelp = this.toggleDiscordantStarsHelp.bind(this);
    this.toggleAscendentSunHelp = this.toggleAscendentSunHelp.bind(this);
    this.toggleFanHyperlanesHelp = this.toggleFanHyperlanesHelp.bind(this);
    this.updateTE = this.updateTE.bind(this);
    this.togglePickFactionsHelp = this.togglePickFactionsHelp.bind(this);
    this.toggleBoardStyleHelp = this.toggleBoardStyleHelp.bind(this);
    this.togglePickStyleHelp = this.togglePickStyleHelp.bind(this);
    this.togglePlacementStyleHelp = this.togglePlacementStyleHelp.bind(this);
    this.toggleSetPlayerNamesHelp = this.toggleSetPlayerNamesHelp.bind(this);
    this.toggleSetFactionsHelp = this.toggleSetFactionsHelp.bind(this);
    this.togglePickMultipleFactionsHelp =
      this.togglePickMultipleFactionsHelp.bind(this);
    this.toggleShufflePriorityHelp = this.toggleShufflePriorityHelp.bind(this);
    this.toggleReversePlacementOrderHelp =
      this.toggleReversePlacementOrderHelp.bind(this);
    this.toggleForceWormholesHelp = this.toggleForceWormholesHelp.bind(this);
    this.toggleForceAnomaliesHelp = this.toggleForceAnomaliesHelp.bind(this);
    this.toggleEnsureFactionAnomaliesHelp =
      this.toggleEnsureFactionAnomaliesHelp.bind(this);
    this.toggleBalancePlanetTraitsHelp =
      this.toggleBalancePlanetTraitsHelp.bind(this);
    this.toggleBalanceSlicesHelp = this.toggleBalanceSlicesHelp.bind(this);
    this.toggleAdvancedSettings = this.toggleAdvancedSettings.bind(this);
  }

  handleInputChange(event) {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    this.setState({
      [name]: value,
    });
  }

  handleNameChange(event) {
    let nameIndex = parseInt(event.target.name.substring(10));
    let newPlayerNames = this.props.currentPlayerNames;
    newPlayerNames[nameIndex] = event.target.value;

    this.setState({
      currentPlayerNames: newPlayerNames,
    });
    this.props.updatePlayerNames(newPlayerNames);
  }

  handleFactionsChange(event) {
    let faction = event.target.name;
    let newCurrentFactions = this.props.currentFactions;
    if (faction === "none") {
      // The deselect all option
      newCurrentFactions = [];
    } else if (faction === "all") {
      // The deselect all option
      newCurrentFactions = [...this.state.optionsPossible.factions];
      if (this.props.includedExpansions[EXPANSIONS.POK]) {
        newCurrentFactions = newCurrentFactions.concat(
          this.state.optionsPossible.pokFactions,
        );
      }
      if (this.props.includedExpansions[EXPANSIONS.TE]) {
        newCurrentFactions = newCurrentFactions.concat(
          this.state.optionsPossible.teFactions,
        );
      }
      if (this.props.includedExpansions[EXPANSIONS.DS]) {
        newCurrentFactions = newCurrentFactions.concat(
          this.state.optionsPossible.dsFactions,
        );
      }
    } else {
      let indexToToggle = newCurrentFactions.indexOf(faction);
      if (indexToToggle >= 0) {
        newCurrentFactions.splice(indexToToggle, 1);
      } else {
        newCurrentFactions.push(faction);
      }
    }

    this.props.updateFactions(newCurrentFactions);
  }

  updatePok(event) {
    let factions = [...this.state.optionsPossible.factions];
    if (this.props.includedExpansions[EXPANSIONS.TE])
      factions = factions.concat(this.state.optionsPossible.teFactions);
    if (this.props.includedExpansions[EXPANSIONS.DS])
      factions = factions.concat(this.state.optionsPossible.dsFactions);
    let boardOptions = this.state.optionsPossible.boardStyles;
    if (event.target.checked) {
      boardOptions = this.state.optionsPossible.boardStylesPok;
      this.setState(
        {
          currentNumberOfPlayersOptions:
            this.state.optionsPossible.numberOfPlayers.concat(
              this.state.optionsPossible.pokNumberOfPlayers,
            ),
          currentBoardStyle: boardOptions[this.state.currentNumberOfPlayers][0],
          currentBoardStyleOptions:
            boardOptions[this.state.currentNumberOfPlayers],
        },
        () => {
          this.props.updateFactions([
            ...factions.concat(this.state.optionsPossible.pokFactions),
          ]);
          this.props.toggleProphecyOfKings(event);
        },
      );
    } else {
      this.setState(
        {
          currentNumberOfPlayers:
            this.state.currentNumberOfPlayers > 6
              ? 6
              : this.state.currentNumberOfPlayers,
          currentNumberOfPlayersOptions:
            this.state.optionsPossible.numberOfPlayers,
          currentBoardStyle:
            this.state.currentNumberOfPlayers > 6
              ? boardOptions["6"][0]
              : this.state.currentBoardStyle,
          currentBoardStyleOptions:
            this.state.currentNumberOfPlayers > 6
              ? boardOptions["6"]
              : boardOptions[this.state.currentNumberOfPlayers],
        },
        () => {
          this.props.updateFactions(factions);
          this.props.toggleProphecyOfKings(event);
        },
      );
    }
  }

  updateUncharted(event) {
    this.props.toggleUnchartedSpace(event);
  }

  updateSun(event) {
    this.props.toggleAscendentSun(event);
  }

  updateFanHyp(event) {
    this.props.toggleFanHyperlanes(event);
  }

  updateTE(event) {
    let factions = [...this.state.optionsPossible.factions];
    if (this.props.includedExpansions[EXPANSIONS.POK])
      factions = factions.concat(this.state.optionsPossible.pokFactions);
    if (this.props.includedExpansions[EXPANSIONS.DS])
      factions = factions.concat(this.state.optionsPossible.dsFactions);
    if (event.target.checked) {
      this.props.updateFactions([
        ...factions.concat(this.state.optionsPossible.teFactions),
      ]);
    } else {
      this.props.updateFactions(factions);
    }
    this.props.toggleThundersEdge(event);
  }

  updateDS(event) {
    let factions = [...this.state.optionsPossible.factions];
    if (this.props.includedExpansions[EXPANSIONS.POK])
      factions = factions.concat(this.state.optionsPossible.pokFactions);
    if (this.props.includedExpansions[EXPANSIONS.TE])
      factions = factions.concat(this.state.optionsPossible.teFactions);
    if (event.target.checked) {
      this.props.updateFactions([
        ...factions.concat(this.state.optionsPossible.dsFactions),
      ]);
    } else {
      this.props.updateFactions(factions);
    }
    this.props.toggleDiscordantStars(event);
  }

  updatePlayerCount(event) {
    this.setState(
      {
        currentNumberOfPlayers: parseInt(event.target.value),
      },
      () => {
        this.updateBoardStyleOptions(event);
      },
    );
  }

  updateBoardStyle(event) {
    this.setState(
      {
        currentBoardStyle: event.target.value,
      },
      () => {
        if (this.state.generated) {
          this.generateBoard(event);
        }
      },
    );
  }

  updateBoardStyleOptions(event) {
    let boardOptions = this.state.optionsPossible.boardStyles;
    if (this.props.includedExpansions[EXPANSIONS.POK]) {
      boardOptions = this.state.optionsPossible.boardStylesPok;
    }
    this.setState(
      {
        currentBoardStyleOptions:
          boardOptions[this.state.currentNumberOfPlayers],
        currentBoardStyle: boardOptions[this.state.currentNumberOfPlayers][0],
      },
      () => {
        if (this.state.generated) {
          this.generateBoard(event);
        }
      },
    );
  }

  updateSeed(event) {
    let newSeed = parseInt(event.target.value);
    if (!isNaN(newSeed) || event.target.value === "") {
      this.setState({
        currentSeed: event.target.value,
        userSetSeed: event.target.value !== "", // Ignore user entry if the value is blank
      });
    }
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  shuffle(array) {
    let m = array.length,
      t,
      i;
    let seed = this.state.currentSeed.valueOf();

    // While there remain elements to shuffle…
    while (m) {
      // Pick a remaining element…
      i = Math.floor(this.random(seed) * m--);

      // And swap it with the current element.
      t = array[m];
      array[m] = array[i];
      array[i] = t;
      ++seed;
    }

    return array;
  }

  random(seed) {
    let x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  }

  encodeSettings() {
    let encodedSettings = "";

    encodedSettings += this.props.includedExpansions[EXPANSIONS.POK]
      ? "T"
      : "F";
    encodedSettings += this.props.includedExpansions[EXPANSIONS.TE] ? "T" : "F";
    if (this.state.fanContent) {
      encodedSettings += this.props.includedExpansions[EXPANSIONS.UnS]
        ? "T"
        : "F";
      encodedSettings += this.props.includedExpansions[EXPANSIONS.DS]
        ? "T"
        : "F";
      encodedSettings += this.props.includedExpansions[EXPANSIONS.AS]
        ? "T"
        : "F";
      encodedSettings += this.props.includedExpansions[EXPANSIONS.Async]
        ? "T"
        : "F";
    } else {
      encodedSettings += "FFFF";
    }
    encodedSettings += this.state.currentNumberOfPlayers.toString();
    encodedSettings += this.state.currentBoardStyleOptions
      .indexOf(this.state.currentBoardStyle)
      .toString();
    encodedSettings += this.state.optionsPossible.placementStyles
      .indexOf(this.state.currentPlacementStyle)
      .toString();
    encodedSettings += this.state.optionsPossible.pickStyles
      .indexOf(this.state.currentPickStyle)
      .toString();
    if (this.state.currentPickStyle === "custom") {
      encodedSettings += this.state.resourceWeight.toString().padStart(3, "0");
      encodedSettings += this.state.influenceWeight.toString().padStart(3, "0");
      encodedSettings += this.state.planetCountWeight
        .toString()
        .padStart(3, "0");
      encodedSettings += this.state.specialtyWeight.toString().padStart(3, "0");
      encodedSettings += this.state.anomalyWeight.toString().padStart(3, "0");
      encodedSettings += this.state.wormholeWeight.toString().padStart(3, "0");
    }
    encodedSettings += this.state.currentSeed.toString().padStart(4, "0");
    encodedSettings += this.state.shuffleBoards ? "T" : "F";
    encodedSettings += this.state.reversePlacementOrder ? "T" : "F";
    encodedSettings += this.state.forceWormholes ? "T" : "F";
    encodedSettings += this.state.forceAnomalies ? "T" : "F";
    encodedSettings += this.state.balancePlanetTraits ? "T" : "F";
    encodedSettings += this.state.balanceSlices ? "T" : "F";
    encodedSettings += this.state.pickFactions ? "T" : "F";
    if (this.state.pickFactions) {
      encodedSettings += this.state.ensureFactionAnomalies ? "T" : "F";
      let combinedFactions = this.state.optionsPossible.factions
        .concat(this.state.optionsPossible.pokFactions)
        .concat(this.state.optionsPossible.dsFactions)
        .concat(this.state.optionsPossible.teFactions);
      let expectedFactions = 17;
      if (this.props.includedExpansions[EXPANSIONS.POK]) expectedFactions += 7;
      if (this.props.includedExpansions[EXPANSIONS.DS]) expectedFactions += 34;
      if (this.props.includedExpansions[EXPANSIONS.TE]) expectedFactions += 5;
      if (this.props.currentFactions.length !== expectedFactions) {
        // Need to encode all factions, because it is not the default
        for (let faction of this.props.currentFactions) {
          if (combinedFactions.indexOf(faction) >= 0) {
            encodedSettings += combinedFactions
              .indexOf(faction)
              .toString()
              .padStart(2, "0");
          }
        }
      }
      let changedPlayerName = false;
      let playerNames = "|";
      for (let playerName of this.props.currentPlayerNames) {
        if (playerName !== "") {
          changedPlayerName = true;
        }
        playerNames += playerName + "|";
      }
      if (changedPlayerName) {
        encodedSettings += playerNames;
        encodedSettings = encodedSettings.slice(0, -1);
      }
    }

    return encodedSettings;
  }

  decodeSettings(newSettings, newTiles) {
    let currentIndex = 0;

    console.log(newSettings);

    let useExpansions = Object.fromEntries([
      [EXPANSIONS.POK, false],
      [EXPANSIONS.UnS, false],
      [EXPANSIONS.DS, false],
      [EXPANSIONS.AS, false],
      [EXPANSIONS.Async, false],
      [EXPANSIONS.TE, false],
    ]);
    let useFanContent = false;

    // Prophecy of Kings
    useExpansions[EXPANSIONS.POK] = newSettings[currentIndex] === "T";
    currentIndex += 1;

    // Thunder's Edge, Fan Content, Number of Players, and Board Style.
    // Thunder's Edge is only present in links generated after that
    // expansion was added, but a single-character T/F flag is
    // indistinguishable from the fan content flags that follow it, so we
    // can't tell from the character alone whether it's present. Instead we
    // optimistically assume it's present (the current/common case) and
    // validate the resulting board style; if that parse doesn't produce a
    // valid board style, we retry assuming the flag is absent, which is
    // what old shared links (created before Thunder's Edge existed) use.
    const parseExpansionsAndBoardStyle = (consumeTE) => {
      let index = currentIndex;
      let expansions = { ...useExpansions };
      let fanContent = false;

      if (consumeTE) {
        expansions[EXPANSIONS.TE] = newSettings[index] === "T";
        index += 1;
      } else {
        expansions[EXPANSIONS.TE] = false;
      }

      // Fan Content
      // Compatability with old URL formatting
      if (newSettings[index] === "T" || newSettings[index] === "F") {
        expansions[EXPANSIONS.UnS] = newSettings[index] === "T";
        fanContent = fanContent || expansions[EXPANSIONS.UnS];
        index += 1;
        expansions[EXPANSIONS.DS] = newSettings[index] === "T";
        fanContent = fanContent || expansions[EXPANSIONS.DS];
        index += 1;
        expansions[EXPANSIONS.AS] = newSettings[index] === "T";
        fanContent = fanContent || expansions[EXPANSIONS.AS];
        index += 1;
        expansions[EXPANSIONS.Async] = newSettings[index] === "T";
        fanContent = fanContent || expansions[EXPANSIONS.Async];
        index += 1;
      }

      // Number of Players
      let numberOfPlayers = Number(newSettings[index]);
      index += 1;
      let numberOfPlayersOptions = expansions[EXPANSIONS.POK]
        ? this.state.optionsPossible.numberOfPlayers.concat(
            this.state.optionsPossible.pokNumberOfPlayers,
          )
        : this.state.optionsPossible.numberOfPlayers;

      // Board Style
      let boardStyleOptions = expansions[EXPANSIONS.POK]
        ? this.state.optionsPossible.boardStylesPok[numberOfPlayers]
        : this.state.optionsPossible.boardStyles[numberOfPlayers];
      let boardStyle = boardStyleOptions
        ? boardStyleOptions[Number(newSettings[index])]
        : undefined;
      index += 1;

      return {
        expansions,
        fanContent,
        index,
        numberOfPlayers,
        numberOfPlayersOptions,
        boardStyleOptions,
        boardStyle,
        valid: boardStyleOptions !== undefined && boardStyle !== undefined,
      };
    };

    let parsedSettings = parseExpansionsAndBoardStyle(true);
    if (!parsedSettings.valid) {
      parsedSettings = parseExpansionsAndBoardStyle(false);
    }

    useExpansions = parsedSettings.expansions;
    useFanContent = parsedSettings.fanContent;
    currentIndex = parsedSettings.index;
    let currentNumberOfPlayers = parsedSettings.numberOfPlayers;
    let currentNumberOfPlayersOptions = parsedSettings.numberOfPlayersOptions;
    let currentBoardStyleOptions = parsedSettings.boardStyleOptions;
    let currentBoardStyle = parsedSettings.boardStyle;

    // Placement Style
    let currentPlacementStyle =
      this.state.optionsPossible.placementStyles[
        Number(newSettings[currentIndex])
      ];
    currentIndex += 1;

    // Pick Style
    let currentPickStyle =
      this.state.optionsPossible.pickStyles[Number(newSettings[currentIndex])];
    currentIndex += 1;

    let resourceWeight = this.state.resourceWeight;
    let influenceWeight = this.state.influenceWeight;
    let planetCountWeight = this.state.planetCountWeight;
    let specialtyWeight = this.state.specialtyWeight;
    let anomalyWeight = this.state.anomalyWeight;
    let wormholeWeight = this.state.wormholeWeight;
    if (currentPickStyle === "custom") {
      resourceWeight = Number(
        newSettings.substring(currentIndex, currentIndex + 3),
      );
      currentIndex += 3;
      influenceWeight = Number(
        newSettings.substring(currentIndex, currentIndex + 3),
      );
      currentIndex += 3;
      planetCountWeight = Number(
        newSettings.substring(currentIndex, currentIndex + 3),
      );
      currentIndex += 3;
      specialtyWeight = Number(
        newSettings.substring(currentIndex, currentIndex + 3),
      );
      currentIndex += 3;
      anomalyWeight = Number(
        newSettings.substring(currentIndex, currentIndex + 3),
      );
      currentIndex += 3;
      wormholeWeight = Number(
        newSettings.substring(currentIndex, currentIndex + 3),
      );
      currentIndex += 3;
    }

    // Current Seed
    let currentSeed = Number(
      newSettings.substring(currentIndex, currentIndex + 4),
    );
    currentIndex += 4;

    // Shuffle Boards
    let shuffleBoards = newSettings[currentIndex] === "T";
    currentIndex += 1;

    // Reverse Placement Order
    let reversePlacementOrder = newSettings[currentIndex] === "T";
    currentIndex += 1;

    // Force Alpha and Beta Wormholes
    let forceWormholes = newSettings[currentIndex] === "T";
    currentIndex += 1;

    // Force All Anomaly Types
    let forceAnomalies = newSettings[currentIndex] === "T";
    currentIndex += 1;

    // Balance Planet Traits
    let balancePlanetTraits = newSettings[currentIndex] === "T";
    currentIndex += 1;

    // Balance Slices
    let balanceSlices = newSettings[currentIndex] === "T";
    currentIndex += 1;

    // Pick Factions
    let pickFactions = newSettings[currentIndex] === "T";
    currentIndex += 1;

    let currentPlayerNames = this.props.currentPlayerNames;
    let currentFactions = this.props.currentFactions;
    let ensureFactionAnomalies = this.props.ensureFactionAnomalies;
    if (pickFactions) {
      // Ensure Faction Anomalies
      ensureFactionAnomalies = newSettings[currentIndex] === "T";
      currentIndex += 1;

      if (newSettings[currentIndex] !== "|") {
        if (newSettings[currentIndex] !== undefined) {
          // Custom factions, but not all of them
          currentFactions = [];
          let combinedFactions = this.state.optionsPossible.factions
            .concat(this.state.optionsPossible.pokFactions)
            .concat(this.state.optionsPossible.dsFactions)
            .concat(this.state.optionsPossible.teFactions);
          while (
            newSettings[currentIndex] !== "|" &&
            currentIndex !== newSettings.length
          ) {
            currentFactions.push(
              combinedFactions[
                Number(newSettings.substring(currentIndex, currentIndex + 2))
              ],
            );
            currentIndex += 2;
          }
        } else {
          currentFactions = this.state.optionsPossible.factions;
          if (useExpansions[EXPANSIONS.POK])
            currentFactions = currentFactions.concat(
              this.state.optionsPossible.pokFactions,
            );
          if (useExpansions[EXPANSIONS.TE])
            currentFactions = currentFactions.concat(
              this.state.optionsPossible.teFactions,
            );
          if (useExpansions[EXPANSIONS.DS])
            currentFactions = currentFactions.concat(
              this.state.optionsPossible.dsFactions,
            );
        }
      } else {
        // No custom factions, but player names are specified, so parse them
        currentPlayerNames = newSettings
          .substring(currentIndex)
          .split("|")
          .splice(1);
      }
    }

    this.setState(
      {
        fanContent: useFanContent,
        currentNumberOfPlayers: currentNumberOfPlayers,
        currentNumberOfPlayersOptions: currentNumberOfPlayersOptions,
        currentBoardStyle: currentBoardStyle,
        currentBoardStyleOptions: currentBoardStyleOptions,
        currentPlacementStyle: currentPlacementStyle,
        currentPickStyle: currentPickStyle,
        resourceWeight: resourceWeight,
        influenceWeight: influenceWeight,
        planetCountWeight: planetCountWeight,
        specialtyWeight: specialtyWeight,
        anomalyWeight: anomalyWeight,
        wormholeWeight: wormholeWeight,
        currentSeed: currentSeed,
        shuffleBoards: shuffleBoards,
        reversePlacementOrder: reversePlacementOrder,
        forceWormholes: forceWormholes,
        forceAnomalies: forceAnomalies,
        balancePlanetTraits: balancePlanetTraits,
        balanceSlices: balanceSlices,
        pickFactions: pickFactions,
        ensureFactionAnomalies: ensureFactionAnomalies,
      },
      () => {
        this.props.updateFactions(currentFactions);
        this.props.updatePlayerNames(currentPlayerNames);

        if (
          (useExpansions[EXPANSIONS.POK] &&
            !this.props.includedExpansions[EXPANSIONS.POK]) ||
          (!useExpansions[EXPANSIONS.POK] &&
            this.props.includedExpansions[EXPANSIONS.POK])
        ) {
          this.props.toggleProphecyOfKings();
        }
        if (
          (useExpansions[EXPANSIONS.UnS] &&
            !this.props.includedExpansions[EXPANSIONS.UnS]) ||
          (!useExpansions[EXPANSIONS.UnS] &&
            this.props.includedExpansions[EXPANSIONS.UnS])
        ) {
          this.props.toggleUnchartedSpace();
        }
        if (
          (useExpansions[EXPANSIONS.AS] &&
            !this.props.includedExpansions[EXPANSIONS.AS]) ||
          (!useExpansions[EXPANSIONS.AS] &&
            this.props.includedExpansions[EXPANSIONS.AS])
        ) {
          this.props.toggleAscendentSun();
        }
        if (
          (useExpansions[EXPANSIONS.Async] &&
            !this.props.includedExpansions[EXPANSIONS.Async]) ||
          (!useExpansions[EXPANSIONS.Async] &&
            this.props.includedExpansions[EXPANSIONS.Async])
        ) {
          this.props.toggleAscendentSun();
        }
        if (
          (useExpansions[EXPANSIONS.DS] &&
            !this.props.includedExpansions[EXPANSIONS.DS]) ||
          (!useExpansions[EXPANSIONS.DS] &&
            this.props.includedExpansions[EXPANSIONS.DS])
        ) {
          this.props.toggleDiscordantStars();
        }
        if (
          (useExpansions[EXPANSIONS.TE] &&
            !this.props.includedExpansions[EXPANSIONS.TE]) ||
          (!useExpansions[EXPANSIONS.TE] &&
            this.props.includedExpansions[EXPANSIONS.TE])
        ) {
          this.props.toggleThundersEdge();
        }

        if (newTiles.length > 0) {
          // Tiles were changed after rendering, so we need to display them
          this.props.updateTiles(newTiles, newSettings);
        } else {
          this.props.updateTiles(
            this.getNewTileSet(currentFactions, useExpansions),
            newSettings,
            false,
          );
        }
      },
    );
  }

  /**
   * Create a new board using a custom seed.
   * @param event The event which triggered this action, to be ignored.
   */
  generateBoard(event) {
    // Don't actually submit the form
    if (event !== undefined) {
      event.preventDefault();
    }

    // Create a random seed to use unless the user has specified one
    let currentSeed = this.state.currentSeed;
    if (!this.state.userSetSeed) {
      currentSeed = Math.floor(Math.random() * Math.floor(9999));
    }

    this.setState(
      {
        currentSeed: currentSeed,
        generated: true,
      },
      () => {
        this.props.updateTiles(
          this.getNewTileSet(),
          this.encodeSettings(),
          true,
        );
      },
    );
  }

  /**
   * Create a set of new tiles for the board based on the user's input.
   */
  getNewTileSet(currentFactions, includedExpansions) {
    if (includedExpansions === undefined) {
      includedExpansions = Object.fromEntries([
        [EXPANSIONS.POK, this.props.includedExpansions[EXPANSIONS.POK]],
        [
          EXPANSIONS.UnS,
          this.state.fanContent &&
            this.props.includedExpansions[EXPANSIONS.UnS],
        ],
        [
          EXPANSIONS.AS,
          this.state.fanContent && this.props.includedExpansions[EXPANSIONS.AS],
        ],
        [
          EXPANSIONS.Async,
          this.state.fanContent &&
            this.props.includedExpansions[EXPANSIONS.Async],
        ],
        [EXPANSIONS.TE, this.props.includedExpansions[EXPANSIONS.TE]],
      ]);
    }
    // else {
    //     if (includedExpansions[EXPANSIONS.POK] === undefined) {
    //         includedExpansions[EXPANSIONS.POK] = this.props.includedExpansions[EXPANSIONS.POK]
    //     }

    //     if (includedExpansions[EXPANSIONS.UnS] === undefined) {
    //         includedExpansions[EXPANSIONS.UnS] = this.props.includedExpansions[EXPANSIONS.UnS]
    //     }

    //     if (includedExpansions[EXPANSIONS.AS] === undefined) {
    //         includedExpansions[EXPANSIONS.AS] = this.props.includedExpansions[EXPANSIONS.AS]
    //     }

    //     if (includedExpansions[EXPANSIONS.Async] === undefined) {
    //         includedExpansions[EXPANSIONS.Async] = this.props.includedExpansions[EXPANSIONS.Async]
    //     }
    // }

    // Get an ordered list of board spaces that need to have non-home systems assigned to them
    let systemIndexes = this.getNewTilesToPlace();

    // Get current factions for placing factions, and shuffle them around
    if (currentFactions === undefined) {
      currentFactions = [...this.props.currentFactions];
    } else {
      currentFactions = [...currentFactions];
    }
    this.shuffle(currentFactions);
    currentFactions = currentFactions.slice(
      0,
      this.state.currentNumberOfPlayers,
    );

    // Get a set of systems to make the board with, ordered based on user supplied weights
    let newSystems = this.getNewSystemsToPlace(
      systemIndexes.length,
      currentFactions,
      includedExpansions,
    );

    // Copy a blank map to add to
    let newTiles = [...boardData.blankMap];

    // Put Mecatol Rex in the middle
    newTiles[0] = 18;

    // Place hyperlanes
    this.placeHyperlanes(newTiles);

    // Place home planets
    this.placeHomeSystems(newTiles, currentFactions);

    // Place planets one at a time, using the indexes to place combined with the ordered planet list
    for (let systemIndex of systemIndexes) {
      newTiles[systemIndex] = newSystems.shift();
    }

    // Place any systems that were locked from the previous generation
    this.placeLockedSystems(newTiles);

    // Planets have been placed, time to do post processing checks to make sure things are good to go.
    this.checkAdjacencies(newTiles, includedExpansions);

    // Even out what each player can reach, without undoing the adjacency fixes above
    if (this.state.balanceSlices) {
      this.balanceSlices(newTiles, includedExpansions);
    }

    // Update the generated flag then update the tiles
    return newTiles;
  }

  /**
   * Generate a list of index on the board that need to be filled with tiles. The various inputs include:
   * Board Data: Information about what this board looks like, the tiles split into 3 groupings of importance
   * Random Priority: Whether the first player goes first, or who gets the various picks is randomized
   * Placement Style: How the groupings of tiles should be added to the board
   * Reverse Placement Order: Whether the groupsings should be added in reverse order
   * @returns {number[]} A list of indexes on the board that need to be filled in with system tiles
   */
  getNewTilesToPlace() {
    // Copy all the tile arrays from board data
    let primary = [
      ...boardData.styles[this.state.currentNumberOfPlayers.toString()][
        this.state.currentBoardStyle
      ]["primary_tiles"],
    ];
    let secondary = [
      ...boardData.styles[this.state.currentNumberOfPlayers.toString()][
        this.state.currentBoardStyle
      ]["secondary_tiles"],
    ];
    let tertiary = [
      ...boardData.styles[this.state.currentNumberOfPlayers.toString()][
        this.state.currentBoardStyle
      ]["tertiary_tiles"],
    ];

    // If shuffling, we need to shuffle primary, secondary, and tertiary indexes.
    if (this.state.shuffleBoards) {
      this.shuffle(primary);
      this.shuffle(secondary);
      this.shuffle(tertiary);
    }

    // Based on the placement style, generate the list of indexes to place
    let systemIndexes = [];
    switch (this.state.currentPlacementStyle) {
      case "random":
        // Add them all together and shuffle them
        if (this.state.reversePlacementOrder) {
          systemIndexes = tertiary.concat(secondary).concat(primary);
        } else {
          systemIndexes = primary.concat(secondary).concat(tertiary);
        }
        this.shuffle(systemIndexes);
        break;
      case "initial":
        // Primary tiles are separate from the secondary shuffled tiles
        if (this.state.reversePlacementOrder) {
          systemIndexes = this.shuffle(secondary.concat(tertiary)).concat(
            primary,
          );
        } else {
          systemIndexes = primary.concat(
            this.shuffle(secondary.concat(tertiary)),
          );
        }
        break;
      case "home":
        // Primary tiles are tiles adjacent to home systems
        systemIndexes = primary.concat(secondary).concat(tertiary);

        // Get home systems
        let home = [
          ...boardData.styles[this.state.currentNumberOfPlayers.toString()][
            this.state.currentBoardStyle
          ]["home_worlds"],
        ];
        let newPrimary = [];
        for (let homeSystem of home) {
          // Loop over adjacent systems
          let adjacentSystems = adjacencyData[homeSystem];
          for (let adjacentSystem of adjacentSystems) {
            // Remove the adjacent system from the complete list (if it exists), and add it to the front
            if (systemIndexes.indexOf(adjacentSystem) >= 0) {
              newPrimary.push(adjacentSystem);
              systemIndexes.splice(systemIndexes.indexOf(adjacentSystem), 1);
            }
          }
        }

        // Add the adjacent planets to the front of the list
        this.shuffle(newPrimary);
        if (this.state.reversePlacementOrder) {
          systemIndexes = systemIndexes.concat(newPrimary);
        } else {
          systemIndexes = newPrimary.concat(systemIndexes);
        }
        break;
      case "slice":
      default:
        // Combine them in their listed order
        if (this.state.reversePlacementOrder) {
          systemIndexes = tertiary.concat(secondary).concat(primary);
        } else {
          systemIndexes = primary.concat(secondary).concat(tertiary);
        }
        break;
    }

    return systemIndexes;
  }

  /**
   *
   * Get an array of system tile numbers to place on the board.
   * @param {number} numberOfSystems The number of systems to get.
   * @param {boolean} includedExpansions Dictionary of expansions to include based on the enum EXPANSIONS
   * @returns {[]}
   */
  getNewSystemsToPlace(numberOfSystems, currentFactions, includedExpansions) {
    // Pick our a random set of systems, following the needed number of anomalies
    let allBlues = tileData.blue.filter(expansionCheck(includedExpansions));
    let allReds = tileData.red.filter(expansionCheck(includedExpansions));

    // Remove excluded tiles
    for (let system of this.props.excludedTiles) {
      if (allReds.indexOf(system) >= 0) {
        allReds.splice(allReds.indexOf(system), 1);
      } else if (allBlues.indexOf(system) >= 0) {
        allBlues.splice(allBlues.indexOf(system), 1);
      }
    }

    // Count each wormhole type (minus delta), randomly select 3 to include in map gen out of those with 2+ options
    const excludedWormholes = [];
    for (const wormhole in WORMHOLES) {
      if (WORMHOLES[wormhole] === WORMHOLES.DELTA) continue;
      if (
        this.state.forceWormholes &&
        (WORMHOLES[wormhole] === WORMHOLES.ALPHA ||
          WORMHOLES[wormhole] === WORMHOLES.BETA)
      ) {
        // Alpha and Beta are forced to be included, so they are never candidates for random exclusion
        continue;
      }

      let wormholeCount = allReds.filter((systemID) =>
        tileData.all[systemID].wormhole.includes(WORMHOLES[wormhole]),
      ).length;
      wormholeCount += allBlues.filter((systemID) =>
        tileData.all[systemID].wormhole.includes(WORMHOLES[wormhole]),
      ).length;

      if (wormholeCount <= 1) {
        allReds = allReds.filter(
          (systemID) =>
            !tileData.all[systemID].wormhole.includes(WORMHOLES[wormhole]),
        );
        allBlues = allBlues.filter(
          (systemID) =>
            !tileData.all[systemID].wormhole.includes(WORMHOLES[wormhole]),
        );
      } else {
        excludedWormholes.push(wormhole);
      }
    }
    this.shuffle(excludedWormholes);
    excludedWormholes.splice(0, 3);
    for (const wormhole of excludedWormholes) {
      console.log(WORMHOLES[wormhole]);
      allReds = allReds.filter(
        (systemID) =>
          !tileData.all[systemID].wormhole.includes(WORMHOLES[wormhole]),
      );
      allBlues = allBlues.filter(
        (systemID) =>
          !tileData.all[systemID].wormhole.includes(WORMHOLES[wormhole]),
      );
    }

    // Shuffle our tiles for randomness
    this.shuffle(allBlues);
    this.shuffle(allReds);

    // Calculate how many reds we need based on player count
    let blueTileRatio = 2;
    let redTileRatio = 1;

    //If the board style overrides blue and red ratios we use those values, otherwise we go to the switch statement
    const currentBoardStyle =
      boardData.styles[this.state.currentNumberOfPlayers][
        this.state.currentBoardStyle
      ];
    if (currentBoardStyle["overrideRatios"] !== undefined) {
      blueTileRatio = currentBoardStyle["overrideRatios"]["blueTileRatio"];
      redTileRatio = currentBoardStyle["overrideRatios"]["redTileRatio"];
    } else {
      // For 3, 4, and 6 player games, there is a different ratio
      switch (this.state.currentNumberOfPlayers) {
        case 3:
          blueTileRatio = 3;
          redTileRatio = 1;
          break;
        case 4:
          blueTileRatio = 5;
          redTileRatio = 3;
          break;
        case 6:
          blueTileRatio = 3;
          redTileRatio = 2;
          break;
        default:
          break;
      }
    }

    let redsToPlace = Math.round(
      Number((numberOfSystems / (blueTileRatio + redTileRatio)) * redTileRatio),
    );
    let bluesToPlace = Math.round(
      Number(
        (numberOfSystems / (blueTileRatio + redTileRatio)) * blueTileRatio,
      ),
    );

    // If planet heavy system, then reduce number of anomalies
    // if (false) {
    //     let newRedsToPlace = 2; // TODO make this user configurable
    //     if (newRedsToPlace > redsToPlace) {
    //         bluesToPlace = bluesToPlace - (newRedsToPlace - redsToPlace);
    //     } else if (newRedsToPlace < redsToPlace) {
    //         bluesToPlace = bluesToPlace + (redsToPlace - newRedsToPlace);
    //     }
    //     redsToPlace = newRedsToPlace
    // }

    // Start to get the new systems
    let newSystems = [];

    // Force add in the included tiles
    for (let system of this.props.includedTiles) {
      if (newSystems.indexOf(system) < 0) {
        newSystems.push(system);

        // Remove them from the pool to add
        if (allReds.indexOf(system) >= 0) {
          allReds.splice(allReds.indexOf(system), 1);
          redsToPlace -= 1;
        } else {
          allBlues.splice(allBlues.indexOf(system), 1);
          bluesToPlace -= 1;
        }
      }
    }

    // Force add in the locked tiles
    for (let system of this.props.lockedTiles) {
      if (newSystems.indexOf(system) < 0) {
        newSystems.push(system);

        // Remove them from the pool to add
        if (allReds.indexOf(system) >= 0) {
          allReds.splice(allReds.indexOf(system), 1);
          redsToPlace -= 1;
        } else {
          allBlues.splice(allBlues.indexOf(system), 1);
          bluesToPlace -= 1;
        }
      }
    }

    // These tiles are ensured for factions or by the forced anomaly types, and may not be
    // replaced by the adjacency pass later on
    const ensuredAnomalies = [];
    this.ensuredAnomalies = ensuredAnomalies;

    if (this.state.ensureFactionAnomalies && this.state.pickFactions) {
      currentFactions.forEach((faction) => {
        let anomalies = [];
        let match = false;
        // If The Clan of Saar are in the game, ensure we have an asteroid field
        if (faction === "The Clan of Saar") {
          anomalies = tileData.asteroidFields.filter(
            expansionCheck(includedExpansions),
          );
          match = true;
          // If The Embers of Muaat are in the game, ensure we have a supernova
        } else if (faction === "The Embers of Muaat") {
          anomalies = tileData.supernovas.filter(
            expansionCheck(includedExpansions),
          );
          match = true;
          // If The Empyrean are in the game, ensure we have a nebulae
        } else if (faction === "The Empyrean") {
          anomalies = tileData.nebulae.filter(
            expansionCheck(includedExpansions),
          );
          match = true;
          // If The Vuil'Raith Cabal or Nivyn Star Kings are in the game, ensure we have a gravity rift
        } else if (
          faction === "The Vuil'Raith Cabal" ||
          faction === "The Nivyn Star Kings"
        ) {
          anomalies = tileData.gravityRifts.filter(
            expansionCheck(includedExpansions),
          );
          match = true;
        }
        if (match && redsToPlace > 0) {
          // Check if the tile is already included
          const found = anomalies.some((tile) => newSystems.includes(tile));
          // If not we add it
          if (!found) {
            this.shuffle(anomalies);
            newSystems.push(anomalies[0]);
            ensuredAnomalies.push(anomalies[0]);
            allReds.splice(allReds.indexOf(anomalies[0]), 1);
            redsToPlace -= 1;
          } else {
            ensuredAnomalies.push(
              newSystems.find((system) => anomalies.includes(system)),
            );
          }
        }
      });
    }

    // Force one of every anomaly type onto the board, if enabled
    if (this.state.forceAnomalies) {
      const anomalyTypes = [
        tileData.asteroidFields,
        tileData.supernovas,
        tileData.nebulae,
        tileData.gravityRifts,
      ];
      for (const anomalyType of anomalyTypes) {
        const anomalies = anomalyType.filter(
          expansionCheck(includedExpansions),
        );

        // Any tile of the type already in the pool satisfies it, so just protect that one
        const existing = newSystems.find((system) =>
          anomalies.includes(system),
        );
        if (existing !== undefined) {
          if (!ensuredAnomalies.includes(existing)) {
            ensuredAnomalies.push(existing);
          }
          continue;
        }

        const candidates = this.shuffle(
          anomalies.filter(
            (tile) => allReds.includes(tile) || allBlues.includes(tile),
          ),
        );
        while (candidates.length > 0) {
          const tile = candidates.pop();
          if (allReds.includes(tile)) {
            if (redsToPlace <= 0) continue;
            allReds.splice(allReds.indexOf(tile), 1);
            redsToPlace -= 1;
          } else {
            if (bluesToPlace <= 0) continue;
            allBlues.splice(allBlues.indexOf(tile), 1);
            bluesToPlace -= 1;
          }
          newSystems.push(tile);
          ensuredAnomalies.push(tile);
          break;
        }
      }
    }

    // Force Alpha and Beta wormholes to actually be placed on the board, if enabled
    if (this.state.forceWormholes) {
      for (const wormholeType of [WORMHOLES.ALPHA, WORMHOLES.BETA]) {
        const allWormholesOfType = tileData[`${wormholeType}Wormholes`].filter(
          expansionCheck(includedExpansions),
        );
        let existingCount = 0;
        for (const tile of allWormholesOfType) {
          if (newSystems.includes(tile)) existingCount += 1;
        }
        let neededCount = 2 - existingCount;
        if (neededCount <= 0) continue;

        const candidates = this.shuffle(
          allWormholesOfType.filter(
            (tile) => allReds.includes(tile) || allBlues.includes(tile),
          ),
        );
        while (neededCount > 0 && candidates.length > 0) {
          const tile = candidates.pop();
          if (allReds.includes(tile)) {
            if (redsToPlace <= 0) continue;
            allReds.splice(allReds.indexOf(tile), 1);
            redsToPlace -= 1;
          } else {
            if (bluesToPlace <= 0) continue;
            allBlues.splice(allBlues.indexOf(tile), 1);
            bluesToPlace -= 1;
          }
          newSystems.push(tile);
          neededCount -= 1;
        }
      }
    }

    // Place as many as possible from tile type if there is not enough to place
    if (redsToPlace > allReds.length) {
      newSystems = newSystems.concat(
        allBlues
          .slice(0, bluesToPlace + (redsToPlace - allReds.length))
          .concat(allReds),
      );
    } else if (bluesToPlace > allBlues.length) {
      newSystems = newSystems.concat(
        allBlues.concat(
          allReds.slice(0, redsToPlace + (bluesToPlace - allBlues.length)),
        ),
      );
    } else {
      newSystems = newSystems.concat(
        allBlues.slice(0, bluesToPlace).concat(allReds.slice(0, redsToPlace)),
      );
    }

    // Ensure that if a wormhole is included, two are (except gamma)
    for (const wormhole in WORMHOLES) {
      if (WORMHOLES[wormhole] === WORMHOLES.GAMMA) continue;
      if (WORMHOLES[wormhole] === WORMHOLES.DELTA) continue;
      const allWormholesOfType = tileData[
        `${WORMHOLES[wormhole]}Wormholes`
      ].filter(expansionCheck(includedExpansions));
      newSystems = this.ensureWormholesForType(
        newSystems,
        allWormholesOfType,
        ensuredAnomalies,
        includedExpansions,
      );
    }
    // Second pass for any multi-wormhole systems. This time, remove singletons
    for (const wormhole in WORMHOLES) {
      if (WORMHOLES[wormhole] === WORMHOLES.GAMMA) continue;
      if (WORMHOLES[wormhole] === WORMHOLES.DELTA) continue;
      const allWormholesOfType = tileData[
        `${WORMHOLES[wormhole]}Wormholes`
      ].filter(expansionCheck(includedExpansions));
      newSystems = this.ensureWormholesForType(
        newSystems,
        allWormholesOfType,
        ensuredAnomalies,
        includedExpansions,
        { method: "remove" },
      );
    }

    // Ensure no planet trait (cultural/industrial/hazardous) is left too scarce to score
    if (this.state.balancePlanetTraits) {
      newSystems = this.ensurePlanetTraitBalance(
        newSystems,
        ensuredAnomalies,
        includedExpansions,
      );
    }

    // Based on the system style, order the systems according to their weights
    let weights = {};
    switch (this.state.currentPickStyle) {
      case "random":
        this.shuffle(newSystems);
        return newSystems;
      case "custom":
        weights = {
          resource: parseInt(this.state.resourceWeight),
          influence: parseInt(this.state.influenceWeight),
          planet_count: parseInt(this.state.planetCountWeight),
          specialty: parseInt(this.state.specialtyWeight),
          anomaly: parseInt(this.state.anomalyWeight),
          wormhole: parseInt(this.state.wormholeWeight),
          faction: parseInt(this.state.wormholeWeight) - 5,
        };
        break;
      case "resource":
        weights = {
          resource: 100,
          influence: 10,
          planet_count: 10,
          specialty: 10,
          anomaly: 10,
          wormhole: 10,
          faction: 5,
        };
        break;
      case "influence":
        weights = {
          resource: 10,
          influence: 100,
          planet_count: 10,
          specialty: 10,
          anomaly: 10,
          wormhole: 10,
          faction: 5,
        };
        break;
      case "balanced":
      default:
        if (includedExpansions[EXPANSIONS.POK]) {
          weights = {
            resource: 80,
            influence: 30,
            planet_count: 15,
            specialty: 50,
            anomaly: 40,
            wormhole: 25,
            faction: 20,
          };
        } else {
          weights = {
            resource: 80,
            influence: 30,
            planet_count: 15,
            specialty: 40,
            anomaly: 30,
            wormhole: 25,
            faction: 20,
          };
        }
        break;
    }

    // Re-order the planets based on their weights
    newSystems = this.getWeightedPlanetList(
      newSystems,
      weights,
      ensuredAnomalies,
    );
    return newSystems;
  }
  placeHyperlanes(newTiles) {
    // Place hyperlanes in their specific layout and orientation
    // TODO in the future, can this be random? Can they rotate?
    for (
      let index = 0;
      index <
      boardData.styles[this.state.currentNumberOfPlayers.toString()][
        this.state.currentBoardStyle
      ]["hyperlane_tiles"].length;
      index++
    ) {
      let hyperlaneData =
        boardData.styles[this.state.currentNumberOfPlayers.toString()][
          this.state.currentBoardStyle
        ]["hyperlane_tiles"][index];
      newTiles[hyperlaneData[0]] = hyperlaneData[1] + "-" + hyperlaneData[2];
    }
  }
  placeHomeSystems(newTiles, currentFactions) {
    // Place data for the homeSystems from board data
    for (
      let index = 0;
      index <
      boardData.styles[this.state.currentNumberOfPlayers.toString()][
        this.state.currentBoardStyle
      ]["home_worlds"].length;
      index++
    ) {
      let planetIndex =
        boardData.styles[this.state.currentNumberOfPlayers.toString()][
          this.state.currentBoardStyle
        ]["home_worlds"][index];
      if (this.state.pickFactions && !this.state.pickMultipleFactions) {
        // Convert factions into faction hexes and assign a random faction to a player
        newTiles[planetIndex] =
          factionData.factionToHomeSystemMap[currentFactions[0]];
        currentFactions.shift();
      } else {
        // Set home worlds to 0, factions to be decided later
        newTiles[planetIndex] = 0;
      }
    }
  }
  placeLockedSystems(newTiles) {
    for (let system of this.props.lockedTiles) {
      // Get location in current tile set
      let currentLocation = this.props.tiles.indexOf(system);
      if (currentLocation >= 0) {
        // It exists, so get its location on the current board
        let newTilesLocation = newTiles.indexOf(system);

        // Swap the two tiles
        newTiles[newTilesLocation] = newTiles[currentLocation];
        newTiles[currentLocation] = system;
      }
    }
  }
  /**
   * Check that anomalies and wormholes are not adjacent, and if so swap them with other tiles.
   * Adjacency here is the rules definition, so systems joined by a hyperlane count as adjacent
   * even though they aren't physically touching (see getAdjacentPositions).
   * @param {*} newTiles Array of tiles that are on the map currently
   * @param {*} includedExpansions Array of expansions to include
   */
  checkAdjacencies(newTiles, includedExpansions) {
    // Anomalies that generation promised would be on the board, so they may be moved but never
    // swapped out for a tile that was left off it
    const ensuredAnomalies = this.ensuredAnomalies || [];

    // Get all anomalies
    let allTrueAnomalies = tileData.anomaly.filter(
      expansionCheck(includedExpansions),
    );

    let newTileAnomalies = newTiles
      .filter((systemID) => tileData.all[systemID] !== undefined)
      .filter(
        (systemID) =>
          tileData.all[systemID].anomaly.length > 0 &&
          tileData.all[systemID].type !== "green",
      );
    let newTileAnomaliesWithAdjacentAnomalies = [];

    // Get all wormholes currently on map

    let includedWormholes = [];
    for (let tile of newTiles.filter(
      (systemID) => tileData.all[systemID] !== undefined,
    )) {
      if (tile === 0 || tile === -1) continue;
      if (tileData.all[tile].wormhole.length > 0) {
        includedWormholes = includedWormholes
          .concat(tileData.all[tile].wormhole)
          .filter((v, i, self) => i === self.indexOf(v));
      }
    }

    for (let anomaly of newTileAnomalies) {
      let anomalyTileNumber = newTiles.indexOf(anomaly);

      let adjacentTiles = getAdjacentPositions(newTiles, anomalyTileNumber);
      let adjacentAnomalies = [];

      // Get a list of all adjacent anomalies to this one
      for (let adjacentTileNumber of adjacentTiles) {
        let adjacentTile = newTiles[adjacentTileNumber];
        if (allTrueAnomalies.indexOf(adjacentTile) >= 0) {
          // This tile is an anomaly
          adjacentAnomalies.push(adjacentTile);
        }
      }

      if (adjacentAnomalies.length > 0) {
        newTileAnomaliesWithAdjacentAnomalies.push([
          anomaly,
          adjacentAnomalies,
        ]);
      }
    }

    newTileAnomaliesWithAdjacentAnomalies.sort(
      (a, b) => b[1].length - a[1].length,
    );

    // Look through tiles from most invalid adjacencies to least
    for (let entry of newTileAnomaliesWithAdjacentAnomalies) {
      const anomaly = entry[0];

      // Ignore any anomalies in the locked or include list
      if (this.props.includedTiles.includes(anomaly)) continue;
      if (this.props.lockedTiles.includes(anomaly)) continue;

      let anomalyTileNumber = newTiles.indexOf(anomaly);

      // Double check adjacencies
      let adjacentTiles = getAdjacentPositions(newTiles, anomalyTileNumber);
      let adjacentAnomalies = [];
      // Get a list of all adjacent anomalies to this one
      for (let adjacentTileNumber of adjacentTiles) {
        let adjacentTile = newTiles[adjacentTileNumber];
        if (allTrueAnomalies.indexOf(adjacentTile) >= 0) {
          // This tile is an anomaly
          adjacentAnomalies.push(adjacentTile);
        }
      }
      if (adjacentAnomalies.length <= 0) continue;

      // If tile is in conflict with more than 1 anomaly, see if there is a "blank" anomaly off the board to swap with. if not, then continue
      // If tile is a planet anomaly (other than a supernova), see if it can be replaced with an equally weight planet system.
      let swapped = false;
      if (
        tileData.all[anomaly].planets.length > 0 &&
        !tileData.all[anomaly].anomaly.includes(ANOMALIES.SUPERNOVA)
      ) {
        let blueTiles = tileData.blue.filter(
          expansionCheck(includedExpansions),
        );
        let possibleTiles = [];
        for (let tile of blueTiles.filter(
          (tile) => newTiles.indexOf(tile) < 0,
        )) {
          // Includes tile if not on board and has equal number of planets as tile being replaced
          if (
            tileData.all[tile].planets.length ===
              tileData.all[anomaly].planets.length &&
            tileData.all[tile].wormhole.every((wormhole) =>
              tileData.all[anomaly].wormhole.includes(wormhole),
            ) &&
            tileData.all[anomaly].wormhole.every((wormhole) =>
              tileData.all[tile].wormhole.includes(wormhole),
            )
          ) {
            possibleTiles.push(tile);
          }
        }
        this.shuffle(possibleTiles);
        if (possibleTiles.length > 0 && !ensuredAnomalies.includes(anomaly)) {
          swapped = true;
          newTiles[anomalyTileNumber] = possibleTiles[0];
        }

        // If not equivalent unused system to swap it with
        if (!swapped && adjacentAnomalies.length > 0) {
          // Look at all blue backed tiles on the board that are not anomalies, and see if they have adjacent anomalies
          for (let tile of blueTiles) {
            let tileNumber = newTiles.indexOf(tile);
            if (tileNumber >= 0) {
              let adjacentTiles = getAdjacentPositions(newTiles, tileNumber);
              let swappable = true;
              for (let adjacentTile of adjacentTiles) {
                if (
                  allTrueAnomalies.indexOf(newTiles[adjacentTile]) >= 0 &&
                  adjacentTile !== anomalyTileNumber
                ) {
                  // This system has an adjacent anomaly, so throw it out
                  swappable = false;
                  break;
                }
              }
              if (swappable) {
                // This blank red has no other adjacent anomalies, so swap
                newTiles[anomalyTileNumber] = tile;
                newTiles[tileNumber] = anomaly;
                swapped = true;
                break;
              }
            }
          }
          if (!swapped) {
            console.log("Error! Unable to swap anomaly to a free position.");
          }
        }
      } else {
        let blankReds = tileData.blankRed.filter(
          expansionCheck(includedExpansions),
        );

        let possibleBlanks = [];
        for (let blankRed of blankReds.filter(
          (tile) => newTiles.indexOf(tile) < 0,
        )) {
          // Includes tile if not on board and has matching wormholes
          if (
            tileData.all[blankRed].wormhole.every((wormhole) =>
              tileData.all[anomaly].wormhole.includes(wormhole),
            ) &&
            tileData.all[anomaly].wormhole.every((wormhole) =>
              tileData.all[blankRed].wormhole.includes(wormhole),
            )
          ) {
            possibleBlanks.push(blankRed);
          }
        }
        this.shuffle(possibleBlanks);
        if (possibleBlanks.length > 0 && !ensuredAnomalies.includes(anomaly)) {
          swapped = true;
          newTiles[anomalyTileNumber] = possibleBlanks[0];
        }

        // If not equivalent unused system to swap it with
        if (!swapped && adjacentAnomalies.length > 0) {
          // Look at all red back tiles on the board that are not anomalies, and see if they have adjacent anomalies
          for (let blankRed of blankReds) {
            let blankRedTileNumber = newTiles.indexOf(blankRed);
            if (blankRedTileNumber >= 0) {
              let adjacentTiles = getAdjacentPositions(
                newTiles,
                blankRedTileNumber,
              );
              let swappable = true;
              for (let adjacentTile of adjacentTiles) {
                if (
                  allTrueAnomalies.indexOf(newTiles[adjacentTile]) >= 0 &&
                  adjacentTile !== anomalyTileNumber
                ) {
                  // This blank has an adjacent anomaly, so throw it out
                  swappable = false;
                  break;
                }
              }
              if (swappable) {
                // This blank red has no other adjacent anomalies, so swap
                newTiles[anomalyTileNumber] = blankRed;
                newTiles[blankRedTileNumber] = anomaly;
                swapped = true;
                break;
              }
            }
          }
          if (!swapped) {
            console.log("Error! Unable to swap anomaly to a free position.");
          }
        }
      }
    }

    for (const wormhole in WORMHOLES) {
      const allWormholesOfType = tileData[
        `${WORMHOLES[wormhole]}Wormholes`
      ].filter(expansionCheck(includedExpansions));
      for (let womrhole of allWormholesOfType) {
        let wormholeTileNumber = newTiles.indexOf(womrhole);
        if (
          wormholeTileNumber >= 0 &&
          tileData.all[womrhole].planets.length === 0
        ) {
          // Wormhole exists on the board, and is blank. Check if it is adjacent to another wormhole
          let adjacentTileNumbers = getAdjacentPositions(
            newTiles,
            wormholeTileNumber,
          );
          let adjacentWormhole = false;
          for (let adjacentTileNumber of adjacentTileNumbers) {
            if (allWormholesOfType.indexOf(newTiles[adjacentTileNumber]) >= 0) {
              adjacentWormhole = true;
              break;
            }
          }
          if (adjacentWormhole) {
            // This blank has an adjacent wormhole, so we need to move it. Loop over all blanks to swap with
            let blankReds = tileData.blankRed.filter(
              expansionCheck(includedExpansions),
            );
            // Remove wormholes from blank reds, because swapping alphas doesn't make sense.
            blankReds = blankReds.filter(function (el) {
              return allWormholesOfType.indexOf(el) < 0;
            });
            blankReds = this.shuffle(blankReds);
            for (let blankRed of blankReds) {
              let blankRedTileNumber = newTiles.indexOf(blankRed);
              if (blankRedTileNumber >= 0) {
                let adjacentTilesNumbers = getAdjacentPositions(
                  newTiles,
                  blankRedTileNumber,
                );
                let swappable = true;
                for (let adjacentTileNumber of adjacentTilesNumbers) {
                  if (
                    (allWormholesOfType.indexOf(newTiles[adjacentTileNumber]) >=
                      0 &&
                      adjacentTileNumber !== wormholeTileNumber) ||
                    (allTrueAnomalies.indexOf(womrhole) >= 0 &&
                      allTrueAnomalies.indexOf(newTiles[adjacentTileNumber]) >=
                        0)
                  ) {
                    // This blank has an adjacent anomaly, so throw it out
                    swappable = false;
                    break;
                  }
                }
                if (swappable) {
                  // This blank red has no other adjacent anomalies, so swap
                  newTiles[wormholeTileNumber] = blankRed;
                  newTiles[blankRedTileNumber] = womrhole;
                  break;
                }
              }
            }
          }
        }
      }
    }
  }

  /**
   * Count how many of the given positions break the adjacency rules checkAdjacencies enforces:
   * an anomaly next to another anomaly, or a blank wormhole tile next to another wormhole of the
   * same type. Counting rather than just reporting pass/fail lets balanceSlices keep working on a
   * board that already had a violation it could not place its way out of.
   * @param {*} newTiles Array of tiles that are on the map currently
   * @param {number[]} positions The positions to check
   * @param {*} allTrueAnomalies The anomaly tiles available to this game
   * @returns {number} How many of those positions sit in an illegal spot
   */
  countAdjacencyViolations(newTiles, positions, allTrueAnomalies) {
    let violations = 0;

    for (let position of positions) {
      let tile = newTiles[position];
      if (tileData.all[tile] === undefined) continue;

      let adjacentTileNumbers = getAdjacentPositions(newTiles, position);

      if (allTrueAnomalies.indexOf(tile) >= 0) {
        for (let adjacentTileNumber of adjacentTileNumbers) {
          if (allTrueAnomalies.indexOf(newTiles[adjacentTileNumber]) >= 0) {
            violations += 1;
            break;
          }
        }
      }

      // Only blank wormholes get moved apart, matching the wormhole pass in checkAdjacencies
      if (
        tileData.all[tile].wormhole.length > 0 &&
        tileData.all[tile].planets.length === 0
      ) {
        for (let adjacentTileNumber of adjacentTileNumbers) {
          let adjacentTile = tileData.all[newTiles[adjacentTileNumber]];
          if (adjacentTile === undefined) continue;
          if (
            adjacentTile.wormhole.some((wormhole) =>
              tileData.all[tile].wormhole.includes(wormhole),
            )
          ) {
            violations += 1;
            break;
          }
        }
      }
    }

    return violations;
  }

  /**
   * Even out the slices. Generation places tiles by weight into a fixed list of positions, which
   * balances the *rank* of the tiles each player gets but not their value — a slice can easily end
   * up with twice the resources of the one across the board from it. This walks that back by
   * repeatedly swapping the pair of systems that most reduces how uneven the slices are, stopping
   * when no swap helps any more.
   *
   * Swaps stay within a tier (primary for primary, and so on) so the board keeps the shape its
   * layout intends, and a swap is only taken if it doesn't leave more tiles breaking the anomaly
   * and wormhole adjacency rules than before, so it can't undo checkAdjacencies.
   * @param {*} newTiles Array of tiles that are on the map currently
   * @param {*} includedExpansions Dictionary of expansions to include based on the enum EXPANSIONS
   */
  balanceSlices(newTiles, includedExpansions) {
    const currentBoardStyle =
      boardData.styles[this.state.currentNumberOfPlayers.toString()][
        this.state.currentBoardStyle
      ];
    const homeWorlds = currentBoardStyle["home_worlds"];
    const sliceMap = getSliceMap(newTiles, homeWorlds);
    if (sliceMap.length < 2) return;

    const allTrueAnomalies = tileData.anomaly.filter(
      expansionCheck(includedExpansions),
    );

    // Tiles the user pinned in place stay where they are
    const frozenPositions = new Set(
      this.props.lockedTiles
        .map((system) => newTiles.indexOf(system))
        .filter((position) => position >= 0),
    );

    const tiers = ["primary_tiles", "secondary_tiles", "tertiary_tiles"]
      .map((tier) =>
        currentBoardStyle[tier].filter(
          (position) =>
            !frozenPositions.has(position) &&
            tileData.all[newTiles[position]] !== undefined,
        ),
      )
      .filter((tier) => tier.length > 1);

    let imbalance = getSliceImbalance(getSliceStats(newTiles, sliceMap));

    for (let pass = 0; pass < BALANCE_SLICES_MAX_PASSES; pass++) {
      if (imbalance <= 0) break;

      let bestSwap = null;
      let bestImbalance = imbalance;

      for (let tier of tiers) {
        for (let first = 0; first < tier.length; first++) {
          for (let second = first + 1; second < tier.length; second++) {
            const from = tier[first];
            const to = tier[second];

            // Only positions the affected tiles touch can change adjacency, so check just those
            const affected = new Set([from, to]);
            for (let position of getAdjacentPositions(newTiles, from)) {
              affected.add(position);
            }
            for (let position of getAdjacentPositions(newTiles, to)) {
              affected.add(position);
            }
            const violationsBefore = this.countAdjacencyViolations(
              newTiles,
              affected,
              allTrueAnomalies,
            );

            const swapped = newTiles[from];
            newTiles[from] = newTiles[to];
            newTiles[to] = swapped;

            const newImbalance = getSliceImbalance(
              getSliceStats(newTiles, sliceMap),
            );
            const improves = newImbalance < bestImbalance - BALANCE_EPSILON;
            const staysLegal =
              improves &&
              this.countAdjacencyViolations(
                newTiles,
                affected,
                allTrueAnomalies,
              ) <= violationsBefore;

            newTiles[to] = newTiles[from];
            newTiles[from] = swapped;

            if (staysLegal) {
              bestSwap = [from, to];
              bestImbalance = newImbalance;
            }
          }
        }
      }

      if (bestSwap === null) break;

      const [from, to] = bestSwap;
      const swapped = newTiles[from];
      newTiles[from] = newTiles[to];
      newTiles[to] = swapped;
      imbalance = bestImbalance;
    }
  }

  /**
   * Ensures that for all wormholes of given types that either 0 or 2+ of that wormhole are included.
   * @param {*} possibleTiles A set of tiles chosen for a map gen
   * @param {Int8Array} desiredWormholes The types of wormholes desired to check
   * @param {*} ensuredAnomalies Tiles that must be included if possible
   * @param {*} includedExpansions List of expansions to include
   * @param {Object} [param4={}]
   * @param {string} [param4.method="add"] Can be "add" or "remove". Optional parameter to dictate if singluar wormholes should have a pair added or be replaced with a non-wormhole.
   * @returns {Int8Array} An ordered list of tiles, with singular wormholes no longer existing
   */
  ensureWormholesForType(
    possibleTiles,
    desiredWormholes,
    ensuredAnomalies,
    includedExpansions,
    { method = "add" } = {},
  ) {
    let planetWormholes = desiredWormholes.filter(
      (system) => !tileData.blankRed.includes(system),
    );

    let allAnomalyList = tileData.red.filter(
      expansionCheck(includedExpansions),
    );
    let unusedWormholes = [];
    let unusedRedTiles = allAnomalyList.filter(
      (tile) => !possibleTiles.includes(tile),
    );
    let unusedSinglePlanets = tileData.blue
      .filter(expansionCheck(includedExpansions))
      .filter((systemID) => tileData.all[systemID].planets.length === 1);
    let usedWormholes = [];

    // Get an array of all used and unused wormholes
    for (let wormholeIndex in desiredWormholes) {
      possibleTiles.indexOf(desiredWormholes[wormholeIndex]) < 0
        ? unusedWormholes.push(desiredWormholes[wormholeIndex])
        : usedWormholes.push(desiredWormholes[wormholeIndex]);
    }

    // If there is only one wormhole, then we need to add/remove a new one in
    if (usedWormholes.length === 1) {
      /*
            Lets do some tricky logic here. If we are using a planet wormhole tile, then we want to replace
            another anomaly with one of the other one (pok: two) anomaly wormholes (or with a single planet if removing it). 
            If we are not using the planet tile, then we can try to find a planet to replace with any remaining wormhole tiles.
             */
      if (method === "add") {
        unusedWormholes = this.shuffle(unusedWormholes);
        let excludedTiles = [...tileData.wormholes].concat(ensuredAnomalies);

        // Check that the single used wormhole is not a planet.
        if (planetWormholes.indexOf(usedWormholes[0]) < 0) {
          // Try to replace some other planet with a planet wormhole
          unusedWormholes = planetWormholes;
          excludedTiles = excludedTiles.concat(allAnomalyList);
        } else {
          // Using a planet, so try to replace an anomaly with a wormhole
          let wormholesToUse = [];
          for (let unusedWormhole of unusedWormholes) {
            if (planetWormholes.indexOf(unusedWormhole) < 0) {
              wormholesToUse.push(unusedWormhole);
            }
          }
          unusedWormholes = wormholesToUse;
        }
        possibleTiles = this.reverseReplace(
          possibleTiles,
          1,
          unusedWormholes,
          excludedTiles,
          false,
        );
      } else if (method === "remove") {
        const excludedTiles = possibleTiles.filter(
          (systemID) => systemID !== usedWormholes[0],
        );
        if (planetWormholes.indexOf(usedWormholes[0]) < 0) {
          // Replace wormhole with another red-backed tile
          possibleTiles = this.reverseReplace(
            possibleTiles,
            1,
            unusedRedTiles,
            excludedTiles,
            false,
          );
        } else {
          // Replace wormhole with another single-planet tile
          possibleTiles = this.reverseReplace(
            possibleTiles,
            1,
            unusedSinglePlanets,
            excludedTiles,
            false,
          );
        }
      }
    }

    return possibleTiles;
  }

  ensureAnomalies(possibleTiles, numPlanetsToPlace, includedExpansions) {
    // Only care about the tiles we will actually place
    possibleTiles = possibleTiles.slice(0, numPlanetsToPlace);

    // Check that there is a minimum number of anomalies on the board
    let blueTileRatio = 2;
    let redTileRatio = 1;
    // For 3, 4, and 6 player games, there is a different ratio
    switch (this.state.currentNumberOfPlayers) {
      case 3:
        blueTileRatio = 3;
        redTileRatio = 1;
        break;
      case 4:
        blueTileRatio = 5;
        redTileRatio = 3;
        break;
      case 6:
        blueTileRatio = 3;
        redTileRatio = 2;
        break;
      default:
        break;
    }
    let numAnomaliesLeftToBePlaced =
      (numPlanetsToPlace / (blueTileRatio + redTileRatio)) * redTileRatio;

    // If there is no anomalies to add, then we are done
    if (numAnomaliesLeftToBePlaced < 0) {
      return possibleTiles;
    }

    // Still have to add a certain number of anomalies in. Get a list of possible anomalies we can add to the tile list
    let allAnomalyList = tileData.red.filter(
      expansionCheck(includedExpansions),
    );

    // Remove all current anomalies in use from this list
    let possibleAnomalies = [];
    for (let anomalyIndex in allAnomalyList) {
      if (possibleTiles.indexOf(allAnomalyList[anomalyIndex]) < 0) {
        // Not using this anomaly yet, so we can add it
        possibleAnomalies.push(allAnomalyList[anomalyIndex]);
      } else {
        // Anomaly is already being used
        numAnomaliesLeftToBePlaced -= 1;
      }
    }

    // Randomize, so anomalies we are using are not always the same
    possibleAnomalies = this.shuffle(possibleAnomalies);

    return this.reverseReplace(
      possibleTiles,
      numAnomaliesLeftToBePlaced,
      possibleAnomalies,
      allAnomalyList,
      false,
    );
  }

  /**
   * Ensure that no planet trait (cultural, industrial, hazardous) is left so scarce that
   * a trait-based objective becomes nearly unscorable. Tops up whichever trait is most
   * underrepresented relative to an even three-way split, swapping in tiles from the
   * unused pool in place of tiles that aren't wormholes or ensured faction anomalies.
   * @param possibleTiles {Int8Array} The ordered list of tiles, cut to the needed size
   * @param ensuredAnomalies {Int8Array} Tiles that must be included if possible
   * @param includedExpansions {Object} List of expansions to include
   * @returns {Int8Array} An ordered list of tiles, with underrepresented traits topped up
   */
  ensurePlanetTraitBalance(
    possibleTiles,
    ensuredAnomalies,
    includedExpansions,
  ) {
    const excludedTiles = tileData.wormholes.concat(ensuredAnomalies);
    const traits = [
      PLANET_TRAITS.CULTURAL,
      PLANET_TRAITS.INDUSTRIAL,
      PLANET_TRAITS.HAZARDOUS,
    ];

    // Tally up how far short of an even split each trait is
    const deficits = traits
      .map((trait) => {
        const traitTiles = tileData[`${trait}Tiles`].filter(
          expansionCheck(includedExpansions),
        );
        const currentCount = possibleTiles.filter((id) =>
          traitTiles.includes(id),
        ).length;
        // Allow some slack below a perfectly even three-way split, since tile
        // pools are rarely evenly distributed between the three traits
        const target = Math.ceil((possibleTiles.length / 3) * 0.6);
        return { traitTiles, needed: target - currentCount };
      })
      .sort((a, b) => b.needed - a.needed);

    // Handle the most-deficient trait first so passes don't undo each other
    let updatedTiles = possibleTiles;
    for (const { traitTiles, needed } of deficits) {
      if (needed <= 0) continue;
      const currentTiles = updatedTiles;
      const candidates = this.shuffle(
        traitTiles.filter((id) => !currentTiles.includes(id)),
      );
      updatedTiles = this.reverseReplace(
        currentTiles,
        needed,
        candidates,
        excludedTiles,
        false,
      );
    }

    return updatedTiles;
  }

  /**
   * Replace tiles starting from the lowest weight (bottom of the list being defined by reversPlacementOrder)
   * @param possibleTiles {Int8Array} The ordered list of tiles, cut to the needed size
   * @param numTilesToReplace {Number} The number of tiles we need to try to replace from replacement into possible
   * @param replacementTiles {Int8Array} An array of all the tiles we can pick from to put into possibleTiles
   * @param excludedTiles {Int8Array} An array of tiles we do not want to replace if possible
   * @param reverseBeforeAndAfter {Boolean} Whether we want to reverse the list before and after replacement
   * @returns {Int8Array} An ordered list of tiles, with some tiles being replaced
   */
  reverseReplace(
    possibleTiles,
    numTilesToReplace,
    replacementTiles,
    excludedTiles,
    reverseBeforeAndAfter,
  ) {
    // If we aren't placing in reverse order, we want to start changing the worst tiles (which are usually last)
    if (reverseBeforeAndAfter) {
      possibleTiles = possibleTiles.reverse();
    }
    let currentTileIndex = possibleTiles.length - 1;

    // While there are anomalies to place and a place to put them...
    while (
      numTilesToReplace > 0 &&
      replacementTiles.length > 0 &&
      currentTileIndex > 0
    ) {
      // Check the index to see that it is not an anomaly or a 0 or -1 or 18 or hyperlane
      let tileOfInterest = possibleTiles[currentTileIndex];
      if (
        excludedTiles.indexOf(tileOfInterest) < 0 &&
        tileOfInterest !== -1 &&
        tileOfInterest !== 18 &&
        tileOfInterest !== 0
      ) {
        // It is a replaceable tile, so fill it from the new anomalies list
        possibleTiles[currentTileIndex] = replacementTiles.shift();
        numTilesToReplace -= 1;
      }
      currentTileIndex -= 1;
    }

    // Undo the reverse from before
    if (reverseBeforeAndAfter) {
      possibleTiles = possibleTiles.reverse();
    }

    return possibleTiles;
  }

  // TODO rename from planet to tile
  getWeightedPlanetList(possiblePlanets, weights, ensuredAnomalies) {
    // Generate an array of tuples where the first element is the plant's tile number and the second is its weight
    let planetWeights = [];
    for (let planetTileNumber in possiblePlanets) {
      planetWeights.push([
        possiblePlanets[planetTileNumber],
        this.getWeight(
          possiblePlanets[planetTileNumber],
          weights,
          ensuredAnomalies,
        ),
      ]);
    }

    // Sort the returned list by weight, with higher weighted planets being first
    planetWeights.sort(function (a, b) {
      return b[1] - a[1];
    });

    // Do a little post process shuffling for roughly similar planets
    let currentIndex = 0;
    let currentHighValue = planetWeights[0][1];
    let currentSetToShuffle = [];
    let postPossiblePlanets = [];
    while (currentIndex < planetWeights.length) {
      let planetWeight = planetWeights[currentIndex];
      // Something in this array, lets see if we add to it
      if (planetWeight[1] >= currentHighValue - this.state.shuffleThreshold) {
        // Add this item to the array to shuffle
        currentSetToShuffle.push(planetWeight);
      } else {
        // We are now outside our shuffle range. So shuffle what we have, and add it to the post
        currentSetToShuffle = this.shuffle(currentSetToShuffle);
        postPossiblePlanets = postPossiblePlanets.concat(currentSetToShuffle);
        currentSetToShuffle = [planetWeight];
        currentHighValue = planetWeight[1];
      }

      currentIndex += 1;
    }
    currentSetToShuffle = this.shuffle(currentSetToShuffle);
    postPossiblePlanets = postPossiblePlanets.concat(currentSetToShuffle);

    // Convert from tuple down to just the tile number
    let orderedPlanets = [];
    for (let weightedPlanet in postPossiblePlanets) {
      orderedPlanets.push(postPossiblePlanets[weightedPlanet][0]);
    }

    return orderedPlanets;
  }

  getWeight(planetTileNumber, weights, ensuredAnomalies) {
    let total_weight = 0;
    let tile = tileData.all[planetTileNumber.toString()];

    // Go over all the planets in this tile and add up their values
    for (let planetIndex in tile["planets"]) {
      let planet = tile["planets"][planetIndex];
      total_weight += (planet["resources"] / 4) * weights["resource"];
      total_weight += (planet["influence"] / 4) * weights["influence"];
      total_weight += weights["planet_count"] / 2;
      total_weight +=
        planet["specialty"] && planet["specialty"].length > 0
          ? weights["specialty"]
          : 0;
    }

    // Handle anomalies
    if (tile["type"] === "red") {
      total_weight += weights["anomaly"] + 40;
      // Providing bonuses to these sections mean the other ones are never used
      // if (tile['anomaly'].length === 0
      //     || (tile['anomaly'].length === 0 && (tile['anomaly'].includes('asteroid-field')
      //         || tile['anomaly'].includes('gravity-rift')))) {
      //     // Give an extra boost to these anomalies, as they are easier to cross than others
      //     total_weight += 20;  // Change total back to 30 if using this
      // }
    }
    total_weight += tile["wormhole"].length > 0 ? weights["wormhole"] : 0;
    total_weight +=
      ensuredAnomalies.indexOf(planetTileNumber) > -1 ? weights["faction"] : 0;

    return total_weight;
  }
  toggleFanContentHelp(event) {
    this.setState({
      fanContentHelp: !this.state.fanContentHelp,
    });
  }
  toggleUnchartedSpaceHelp(event) {
    this.setState({
      unchartedSpaceHelp: !this.state.unchartedSpaceHelp,
    });
  }
  toggleDiscordantStarsHelp(event) {
    this.setState({
      discordantStarsHelp: !this.state.discordantStarsHelp,
    });
  }
  toggleAscendentSunHelp(event) {
    this.setState({
      ascendentSunHelp: !this.state.ascendentSunHelp,
    });
  }
  toggleFanHyperlanesHelp(event) {
    this.setState({
      fanHyperlanesHelp: !this.state.fanHyperlanesHelp,
    });
  }
  togglePickFactionsHelp(event) {
    this.setState({
      pickFactionsHelp: !this.state.pickFactionsHelp,
    });
  }
  toggleBoardStyleHelp(event) {
    this.setState({
      boardStyleHelp: !this.state.boardStyleHelp,
    });
  }
  togglePlacementStyleHelp(event) {
    this.setState({
      placementStyleHelp: !this.state.placementStyleHelp,
    });
  }
  togglePickStyleHelp(event) {
    this.setState({
      pickStyleHelp: !this.state.pickStyleHelp,
    });
  }
  toggleSetPlayerNamesHelp(event) {
    this.setState({
      setPlayerNamesHelp: !this.state.setPlayerNamesHelp,
    });
  }
  toggleSetFactionsHelp(event) {
    this.setState({
      setFactionsHelp: !this.state.setFactionsHelp,
    });
  }
  togglePickMultipleFactionsHelp(event) {
    this.setState({
      pickMultipleFactionsHelp: !this.state.pickMultipleFactionsHelp,
    });
  }
  toggleShufflePriorityHelp(event) {
    this.setState({
      shufflePriorityHelp: !this.state.shufflePriorityHelp,
    });
  }
  toggleReversePlacementOrderHelp(event) {
    this.setState({
      reversePlacementOrderHelp: !this.state.reversePlacementOrderHelp,
    });
  }
  toggleForceWormholesHelp(event) {
    this.setState({
      forceWormholesHelp: !this.state.forceWormholesHelp,
    });
  }
  toggleForceAnomaliesHelp(event) {
    this.setState({
      forceAnomaliesHelp: !this.state.forceAnomaliesHelp,
    });
  }
  toggleEnsureFactionAnomaliesHelp(event) {
    this.setState({
      ensureFactionAnomaliesHelp: !this.state.ensureFactionAnomaliesHelp,
    });
  }
  toggleBalancePlanetTraitsHelp(event) {
    this.setState({
      balancePlanetTraitsHelp: !this.state.balancePlanetTraitsHelp,
    });
  }
  toggleBalanceSlicesHelp(event) {
    this.setState({
      balanceSlicesHelp: !this.state.balanceSlicesHelp,
    });
  }

  toggleAdvancedSettings(event) {
    this.setState({
      advancedSettingsOpen: !this.state.advancedSettingsOpen,
    });
  }

  render() {
    return (
      <div id="options" className={this.props.visible ? "" : "d-none"}>
        <div className="title">
          <h4 className="text-center">Generation Options</h4>
        </div>
        <Form id="generateForm" onSubmit={this.generateBoard}>
          <Form.Group className="mb-3 d-flex" controlId="pokExpansion">
            <Form.Check
              name="pokExpansion"
              type="checkbox"
              checked={this.props.includedExpansions[EXPANSIONS.POK]}
              onChange={this.updatePok}
              label="Use POK Tiles"
            />
          </Form.Group>

          <Form.Group className="mb-3 d-flex" controlId="useThundersEdge">
            <Form.Check
              name="useThundersEdge"
              type="checkbox"
              checked={this.props.includedExpansions[EXPANSIONS.TE]}
              onChange={this.updateTE}
              label="Use Thunder's Edge Tiles"
            />
          </Form.Group>

          <Form.Group className="mb-3 d-flex" controlId="fanContent">
            <Form.Check
              name="fanContent"
              type="checkbox"
              checked={this.state.fanContent}
              onChange={this.handleInputChange}
              label="Use Fan-Made Content"
            />
            <QuestionCircle
              className="icon"
              onClick={this.toggleFanContentHelp}
            />
          </Form.Group>
          <Collapse in={this.state.fanContent}>
            <div>
              <div className="card card-body">
                <Form.Group
                  className="mb-3 d-flex"
                  controlId="useUnchartedSpace"
                >
                  <Form.Check
                    name="useUnchartedSpace"
                    type="checkbox"
                    checked={this.props.includedExpansions[EXPANSIONS.UnS]}
                    onChange={this.updateUncharted}
                    label="Use Uncharted Space Fan Tiles"
                  />
                  <QuestionCircle
                    className="icon"
                    onClick={this.toggleUnchartedSpaceHelp}
                  />
                </Form.Group>
                <Form.Group
                  className="mb-3 d-flex"
                  controlId="useDiscordantStars"
                >
                  <Form.Check
                    name="useDiscordantStars"
                    type="checkbox"
                    checked={this.props.includedExpansions[EXPANSIONS.DS]}
                    onChange={this.updateDS}
                    label="Use DS Fan Factions"
                  />
                  <QuestionCircle
                    className="icon"
                    onClick={this.toggleDiscordantStarsHelp}
                  />
                </Form.Group>
                <Form.Group className="mb-3 d-flex" controlId="useAscendentSun">
                  <Form.Check
                    name="useAscendentSun"
                    type="checkbox"
                    checked={this.props.includedExpansions[EXPANSIONS.AS]}
                    onChange={this.updateSun}
                    label="Use Eronous' Fan Tiles"
                  />
                  <QuestionCircle
                    className="icon"
                    onClick={this.toggleAscendentSunHelp}
                  />
                </Form.Group>
                <Form.Group className="d-flex" controlId="useFanHyperlanes">
                  <Form.Check
                    name="useFanHyperlanes"
                    type="checkbox"
                    checked={this.props.includedExpansions[EXPANSIONS.Async]}
                    onChange={this.updateFanHyp}
                    label="Use Async Hyperlane Tiles"
                  />
                  <QuestionCircle
                    className="icon"
                    onClick={this.toggleFanHyperlanesHelp}
                  />
                </Form.Group>
              </div>
            </div>
          </Collapse>

          <Form.Group className="my-3">
            <Form.Label>Number of Players</Form.Label>
            <Form.Select
              className="form-control"
              id="playerCount"
              name="currentNumberOfPlayers"
              value={this.state.currentNumberOfPlayers}
              onChange={this.updatePlayerCount}
            >
              {this.state.currentNumberOfPlayersOptions.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {/* <div className="form-group my-3">
                        <label htmlFor="playerCount">Number of Players</label>
                        <select className="form-control" id="playerCount" name="currentNumberOfPlayers" value={this.state.currentNumberOfPlayers} onChange={this.updatePlayerCount}>
                            {this.state.currentNumberOfPlayersOptions.map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                    </div> */}

          <Form.Group className="my-3">
            <Form.Label className="d-flex">
              Board Style
              <QuestionCircle
                className="icon"
                onClick={this.toggleBoardStyleHelp}
              />
            </Form.Label>
            <Form.Select
              className="form-control"
              id="boardStyle"
              name="currentBoardStyle"
              value={this.state.currentBoardStyle}
              onChange={this.updateBoardStyle}
            >
              {this.state.currentBoardStyleOptions.map((x) => (
                <option key={x} value={x}>
                  {this.capitalize(x)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {/* <div className="form-group my-3">
                        <label htmlFor="boardStyle" className="d-flex">Board Style
                            <QuestionCircle className="icon" onClick={this.toggleBoardStyleHelp} />
                        </label>
                        <select className="form-control" id="boardStyle" name="currentBoardStyle" value={this.state.currentBoardStyle} onChange={this.updateBoardStyle}>
                            {this.state.currentBoardStyleOptions.map((x) => <option key={x} value={x}>{this.capitalize(x)}</option>)}
                        </select>
                    </div> */}

          <Form.Group className="my-3">
            <Form.Label className="d-flex">
              Placement Style
              <QuestionCircle
                className="icon"
                onClick={this.togglePlacementStyleHelp}
              />
            </Form.Label>
            <Form.Select
              className="form-control"
              id="placementStyle"
              name="currentPlacementStyle"
              value={this.state.currentPlacementStyle}
              onChange={this.handleInputChange}
            >
              {this.state.optionsPossible.placementStyles.map((x) => (
                <option key={x} value={x}>
                  {this.capitalize(x)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {/* <div className="form-group my-3">
                        <label htmlFor="placementStyle" className="d-flex">Placement Style
                            <QuestionCircle className="icon" onClick={this.togglePlacementStyleHelp} />
                        </label>
                        <select className="form-control" id="placementStyle" name="currentPlacementStyle" value={this.state.currentPlacementStyle} onChange={this.handleInputChange}>
                            {this.state.optionsPossible.placementStyles.map((x) => <option key={x} value={x}>{this.capitalize(x)}</option>)}
                        </select>
                    </div> */}

          <Form.Group className="my-3">
            <Form.Label className="d-flex">
              System Weighting
              <QuestionCircle
                className="icon"
                onClick={this.togglePickStyleHelp}
              />
            </Form.Label>
            <Form.Select
              className="form-control"
              id="pickStyle"
              name="currentPickStyle"
              value={this.state.currentPickStyle}
              onChange={this.handleInputChange}
            >
              {this.state.optionsPossible.pickStyles.map((x) => (
                <option key={x} value={x}>
                  {this.capitalize(x)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {/* <div className="form-group my-3">
                        <label htmlFor="pickStyle" className="d-flex">System Weighting
                            <QuestionCircle className="icon" onClick={this.togglePickStyleHelp} />
                        </label>
                        <select className="form-control" id="pickStyle" name="currentPickStyle" value={this.state.currentPickStyle} onChange={this.handleInputChange}>
                            {this.state.optionsPossible.pickStyles.map((x) => <option key={x} value={x}>{this.capitalize(x)}</option>)}
                        </select>
                    </div> */}

          <div
            className={
              "ml-2 collapse " +
              (this.state.currentPickStyle === "custom" ? "show" : "")
            }
            id="customPickStyle"
          >
            <div className="card card-body">
              <label htmlFor="customResource">Resource</label>
              <input
                type="range"
                className="custom-range"
                name="resourceWeight"
                value={this.state.resourceWeight}
                onChange={this.handleInputChange}
              />

              <label htmlFor="customInfluence">Influence</label>
              <input
                type="range"
                className="custom-range"
                name="influenceWeight"
                value={this.state.influenceWeight}
                onChange={this.handleInputChange}
              />

              <label htmlFor="customPlanetCount">Planet Count</label>
              <input
                type="range"
                className="custom-range"
                name="planetCountWeight"
                value={this.state.planetCountWeight}
                onChange={this.handleInputChange}
              />

              <label htmlFor="customSpecialty">Specialty</label>
              <input
                type="range"
                className="custom-range"
                name="specialtyWeight"
                value={this.state.specialtyWeight}
                onChange={this.handleInputChange}
              />

              <label htmlFor="customAnomaly">Anomaly</label>
              <input
                type="range"
                className="custom-range"
                name="anomalyWeight"
                value={this.state.anomalyWeight}
                onChange={this.handleInputChange}
              />

              <label htmlFor="customWormhole">Wormhole</label>
              <input
                type="range"
                className="custom-range"
                name="wormholeWeight"
                value={this.state.wormholeWeight}
                onChange={this.handleInputChange}
              />
            </div>
          </div>

          <Form.Group className="mb-3" controlId="seed">
            <Form.Label>Specific Seed</Form.Label>
            <Form.Control
              name="updateSeed"
              type="text"
              placeholder="Enter a number to seed generation..."
              value={this.state.currentSeed}
              onChange={this.updateSeed}
            />
          </Form.Group>

          <Form.Group className="mb-3 d-flex" controlId="pickFactions">
            <Form.Check
              name="pickFactions"
              type="checkbox"
              checked={this.props.pickFactions}
              onChange={this.handleInputChange}
              label="Pick Factions for Players"
            />
            <QuestionCircle
              className="icon"
              onClick={this.togglePickFactionsHelp}
            />
          </Form.Group>
          <Collapse in={this.state.pickFactions}>
            <div>
              <div className="card card-body">
                <button
                  type="button"
                  className="btn btn-outline-primary mb-2"
                  onClick={this.toggleSetFactionsHelp}
                >
                  Set Included Factions
                </button>

                <button
                  type="button"
                  className="btn btn-outline-primary mb-2"
                  onClick={this.toggleSetPlayerNamesHelp}
                >
                  Set Player Names
                </button>

                <Form.Group
                  className="d-flex"
                  controlId="ensureFactionAnomalies"
                >
                  <Form.Check
                    name="ensureFactionAnomalies"
                    type="checkbox"
                    checked={this.props.ensureFactionAnomalies}
                    onChange={this.handleInputChange}
                    label="Ensure Faction Anomalies"
                  />
                  <QuestionCircle
                    className="icon"
                    onClick={this.toggleEnsureFactionAnomaliesHelp}
                  />
                </Form.Group>
              </div>
            </div>
          </Collapse>
          {/* <div className={"ml-2 mb-2 collapse " + (this.state.pickFactions ? "show" : "")} id="pickFactionsCollapse">
                        <div className="card card-body">
                            <button type="button" className="btn btn-outline-primary mb-2" onClick={this.toggleSetFactionsHelp}>Set Included Factions</button>

                            <button type="button" className="btn btn-outline-primary mb-2" onClick={this.toggleSetPlayerNamesHelp}>Set Player Names</button>

                            <Form.Group className="mb-3 d-flex" controlId="ensureFactionAnomalies">
                                <Form.Check name="ensureFactionAnomalies" type="checkbox" checked={this.props.ensureFactionAnomalies} onChange={this.handleInputChange} label="Ensure Faction Anomalies" />
                                <QuestionCircle className="icon" onClick={this.toggleEnsureFactionAnomaliesHelp} />
                            </Form.Group>
                        </div>
                    </div> */}

          <div
            className="d-flex justify-content-between align-items-center my-3"
            style={{ cursor: "pointer" }}
            onClick={this.toggleAdvancedSettings}
            aria-controls="advancedSettings"
            aria-expanded={this.state.advancedSettingsOpen}
          >
            <strong>Advanced Settings</strong>
            {this.state.advancedSettingsOpen ? (
              <ChevronUp className="icon" />
            ) : (
              <ChevronDown className="icon" />
            )}
          </div>
          <Collapse in={this.state.advancedSettingsOpen}>
            <div id="advancedSettings">
              <div className="card card-body">
                <Form.Group className="mb-3 d-flex" controlId="shuffleBoards">
                  <Form.Check
                    name="shuffleBoards"
                    type="checkbox"
                    checked={this.props.shuffleBoards}
                    onChange={this.handleInputChange}
                    label="Randomize Priorities Before Placement"
                  />
                  <QuestionCircle
                    className="icon"
                    onClick={this.toggleShufflePriorityHelp}
                  />
                </Form.Group>

                <Form.Group
                  className="mb-3 d-flex"
                  controlId="reversePlacementOrder"
                >
                  <Form.Check
                    name="reversePlacementOrder"
                    type="checkbox"
                    checked={this.props.reversePlacementOrder}
                    onChange={this.handleInputChange}
                    label="Reverse Placement Order"
                  />
                  <QuestionCircle
                    className="icon"
                    onClick={this.toggleReversePlacementOrderHelp}
                  />
                </Form.Group>

                <Form.Group className="d-flex" controlId="forceWormholes">
                  <Form.Check
                    name="forceWormholes"
                    type="checkbox"
                    checked={this.state.forceWormholes}
                    onChange={this.handleInputChange}
                    label="Force Alpha and Beta Wormholes"
                  />
                  <QuestionCircle
                    className="icon"
                    onClick={this.toggleForceWormholesHelp}
                  />
                </Form.Group>

                <Form.Group className="mb-3 d-flex" controlId="forceAnomalies">
                  <Form.Check
                    name="forceAnomalies"
                    type="checkbox"
                    checked={this.state.forceAnomalies}
                    onChange={this.handleInputChange}
                    label="Force All Anomaly Types"
                  />
                  <QuestionCircle
                    className="icon"
                    onClick={this.toggleForceAnomaliesHelp}
                  />
                </Form.Group>

                <Form.Group
                  className="mb-3 d-flex"
                  controlId="balancePlanetTraits"
                >
                  <Form.Check
                    name="balancePlanetTraits"
                    type="checkbox"
                    checked={this.state.balancePlanetTraits}
                    onChange={this.handleInputChange}
                    label="Balance Planet Traits"
                  />
                  <QuestionCircle
                    className="icon"
                    onClick={this.toggleBalancePlanetTraitsHelp}
                  />
                </Form.Group>

                <Form.Group className="d-flex" controlId="balanceSlices">
                  <Form.Check
                    name="balanceSlices"
                    type="checkbox"
                    checked={this.state.balanceSlices}
                    onChange={this.handleInputChange}
                    label="Balance Slices"
                  />
                  <QuestionCircle
                    className="icon"
                    onClick={this.toggleBalanceSlicesHelp}
                  />
                </Form.Group>
              </div>
            </div>
          </Collapse>

          <SetPlayerNameModal
            visible={this.state.setPlayerNamesHelp}
            currentPlayerNames={this.props.currentPlayerNames}
            hideModal={this.toggleSetPlayerNamesHelp}
            handleNameChange={this.handleNameChange}
          />
          <SetFactionsModal
            visible={this.state.setFactionsHelp}
            factions={this.state.optionsPossible.factions}
            includedExpansions={this.props.includedExpansions}
            pokFactions={this.state.optionsPossible.pokFactions}
            teFactions={this.state.optionsPossible.teFactions}
            dsFactions={this.state.optionsPossible.dsFactions}
            currentFactions={this.props.currentFactions}
            hideModal={this.toggleSetFactionsHelp}
            handleFactionsChange={this.handleFactionsChange}
          />

          <HelpModal
            key={"help-fan-content"}
            visible={this.state.fanContentHelp}
            hideModal={this.toggleFanContentHelp}
            title={"About Fan-Made Content"}
            content={`<p class="alert alert-warning">
                        Highly experimental content ahead. Fan-made content is subject to change, and does not
                        guarantee the same balance and quality as TI4 and its official expansions.
                      </p>
                      <p>
                        Over the years, some fans have created additional content for the game that has become
                        relatively popular. This option lets you add some of the more well-known fan system tiles
                        during map generation.
                      </p>
                      <p class="mb-0">
                        Fan content featured here is created to be compliant with the ${IP_POLICY_LINK}.
                      </p>`}
          />
          <HelpModal
            key={"help-uncharted-space"}
            visible={this.state.unchartedSpaceHelp}
            hideModal={this.toggleUnchartedSpaceHelp}
            title={"About Uncharted Space"}
            content={`<blockquote class="help-quote">
                        A fan-made expansion for Twilight Imperium: Fourth Edition from the team responsible for
                        Discordant Stars. The new content in this pack is intended to expand the variety of available
                        system tiles, exploration effects, relics, and action cards, while preserving official game
                        balance. This project has endeavored to fully comply with the ${IP_POLICY_LINK}.
                      </blockquote>
                      <p>
                        Only the new system tiles are relevant for this tool. This adds 24 system tiles, including
                        5 new legendary planets, some additional empty systems and anomalies to even out system
                        distributions, and one gamma wormhole inside a gravity rift.
                      </p>
                      ${referenceLink(
                        "https://docs.google.com/document/d/10sYWiwVNvdOwDMpvHlbsx4athKwgeTc8usWFMV3Aaas",
                      )}`}
          />
          <HelpModal
            key={"help-discordant-stars"}
            visible={this.state.discordantStarsHelp}
            hideModal={this.toggleDiscordantStarsHelp}
            title={"About Discordant Stars"}
            content={`<blockquote class="help-quote">
                        A fan-made expansion for Twilight Imperium: Fourth Edition that adds 34 additional factions
                        designed by and for the Twilight Imperium community. This project has endeavored to fully
                        comply with the ${IP_POLICY_LINK}.
                      </blockquote>
                      <p>
                        This option only matters if you choose to pick factions for players. It adds these factions
                        to the pool of random factions, includes their home systems in the graphics, and influences
                        faction anomalies.
                      </p>
                      ${referenceLink(
                        "https://docs.google.com/document/d/1214N4Py1NqvkQzFN5YULKiR7rmn1qpf4OdFqb8_vQUg",
                      )}`}
          />
          <HelpModal
            key={"help-ascendent-sun"}
            visible={this.state.ascendentSunHelp}
            hideModal={this.toggleAscendentSunHelp}
            title={"About Ascendant Sun"}
            content={`<p>
                        "Eronous Tiles" is a set of 130+ tiles created by Discord user and artist Eronous. They were
                        initially made for the TI4 async bot, as part of large community games that required very
                        large maps.
                      </p>
                      <p>
                        This set includes 110 new planets, 12 of which are legendary, 6 new wormhole types, and
                        2 planets inside supernovas.
                      </p>
                      ${referenceLink(
                        "https://docs.google.com/spreadsheets/d/1C1a8uc_DT21aPUUF5oQWNb0y9QYZHCVsE5rarEjB8kA",
                      )}`}
          />
          <HelpModal
            key={"help-fan-hyperlanes"}
            visible={this.state.fanHyperlanesHelp}
            hideModal={this.toggleFanHyperlanesHelp}
            title={"About Async Hyperlanes"}
            content={`<p class="mb-0">
                        These are a set of hyperlane tiles that are part of the TI4 async bot, but aren't necessarily
                        part of any particular fan expansion. Feel free to use them however you imagine.
                      </p>`}
          />
          <HelpModal
            key={"help-board"}
            visible={this.state.boardStyleHelp}
            hideModal={this.toggleBoardStyleHelp}
            title={"About Board Style"}
            content={`<p>
                        Board style changes how the tiles are actually laid out on a newly generated map.
                      </p>
                      <p class="mb-0">
                        Changing this gives you different hex layouts, such as different patterns of tiles, usage of
                        hyperlanes, or unorthodox placement of home worlds.
                      </p>`}
          />
          <HelpModal
            key={"help-placement"}
            visible={this.state.placementStyleHelp}
            hideModal={this.togglePlacementStyleHelp}
            title={"About Placement Style"}
            content={`<p>
                        Placement style dictates where important tiles are placed. Most revolve around having at
                        least one tile near the home system with good resources.
                      </p>
                      <ul class="mb-0">
                        <li><b>Slice:</b> Places tiles like a normal player would. Prioritizes a good pathway to
                        Mecatol, and filling in the area around the home system with good tiles.</li>
                        <li><b>Initial:</b> Only guarantees a good tile right in front of the home system (on the way
                        to Mecatol). Everything else is random.</li>
                        <li><b>Home:</b> Prioritizes all of the tiles adjacent to the home system, and everything
                        else is random.</li>
                        <li><b>Random:</b> Shuffles the priority levels completely. No favoritism to tiles near the
                        home system.</li>
                      </ul>`}
          />
          <HelpModal
            key={"help-pick"}
            visible={this.state.pickStyleHelp}
            hideModal={this.togglePickStyleHelp}
            title={"About Pick Style"}
            content={`<p>
                        Pick style determines how tiles are weighted when they are placed on the board. A higher
                        weighted tile means that the hex is more important, and so (depending on the placement style)
                        it is put closer to home worlds to facilitate available assets.
                      </p>
                      <ul class="mb-0">
                        <li><b>Balanced:</b> A custom weight which favors resources and planet count more than
                        anomalies. This more accurately factors in tech specialties and influence as trade-offs to
                        the "Resource" pick.</li>
                        <li><b>Resource:</b> Tiles are ordered primarily by their resource values. Higher resource
                        planets are more coveted, and so are more important.</li>
                        <li><b>Influence:</b> Similar to "Resource", but tiles are ordered primarily by their
                        influence values.</li>
                        <li><b>Random:</b> Tiles are completely randomly ordered. Expect chaotic and unbalanced
                        maps.</li>
                        <li><b>Custom:</b> Enter your own values for balancing trade-offs between various tile
                        qualities.</li>
                      </ul>`}
          />
          <HelpModal
            key={"help-factions"}
            visible={this.state.pickFactionsHelp}
            hideModal={this.togglePickFactionsHelp}
            title={"About Picking Factions"}
            content={`<p>
                        Automatically assigns factions to the players on the board.
                      </p>
                      <p class="mb-0">
                        From the set of factions, turning this on assigns every player a random faction (designated by
                        giving them the home world tile of that faction). You should pick which player sits at a
                        certain position before turning this on.
                      </p>`}
          />
          <HelpModal
            key={"help-multiple"}
            visible={this.state.pickMultipleFactionsHelp}
            hideModal={this.togglePickMultipleFactionsHelp}
            title={"About Picking Multiple Factions"}
            content={`<p>
                        Divides all the factions evenly amongst the players in the game (with no overflow), so that
                        they can choose from a selection instead of being assigned one faction.
                      </p>
                      <p class="mb-0">
                        Some groups prefer to have a draft, where every player is given a few factions to pick
                        between. This lets them play the faction they want, without conflicting with other players
                        over a certain faction.
                      </p>`}
          />
          <HelpModal
            key={"help-priority"}
            visible={this.state.shufflePriorityHelp}
            hideModal={this.toggleShufflePriorityHelp}
            title={"About Shuffling Priority"}
            content={`<p>
                        Randomizes the priority picks for each picking round.
                      </p>
                      <p>
                        Normally when placing tiles, this tool attempts to place them so that player 1 does not always
                        get the best tiles. To do this, it follows the game setup rules: in a 6 player game, player 1
                        gets to place tiles 1 and 12, while player 6 places tiles 6 and 7.
                      </p>
                      <p class="mb-0">
                        Turning this on stops that from happening, and instead completely randomizes the placement
                        order.
                      </p>`}
          />
          <HelpModal
            key={"help-reverse"}
            visible={this.state.reversePlacementOrderHelp}
            hideModal={this.toggleReversePlacementOrderHelp}
            title={"About Reverse Placement Order"}
            content={`<p>
                        Reverses which tiles are placed first in placement order.
                      </p>
                      <p class="mb-0">
                        Tiles are normally placed in priority order (see the shuffling priority help). This reverses
                        that order, so that the last picks come first, which generally has the effect of pushing the
                        more valuable tiles towards the center of the galaxy.
                      </p>`}
          />
          <HelpModal
            key={"help-force-wormholes"}
            visible={this.state.forceWormholesHelp}
            hideModal={this.toggleForceWormholesHelp}
            title={"About Forcing Alpha and Beta Wormholes"}
            content={`<p>
                        Normally, map generation randomly chooses a handful of wormhole types (out of all those
                        eligible) to place on the board, which means Alpha and Beta wormholes are not guaranteed to
                        show up, especially when fan-made wormhole types are enabled.
                      </p>
                      <p class="mb-0">
                        Turning this on ensures that a pair of Alpha wormholes and a pair of Beta wormholes are always
                        included in the generated galaxy.
                      </p>`}
          />
          <HelpModal
            key={"help-force-anomalies"}
            visible={this.state.forceAnomaliesHelp}
            hideModal={this.toggleForceAnomaliesHelp}
            title={"About Forcing All Anomaly Types"}
            content={`<p>
                        Normally, which anomalies show up is left to chance, so a galaxy can easily end up with no
                        nebula or no gravity rift at all. That is a problem for the factions built around a specific
                        anomaly, like The Clan of Saar, The Embers of Muaat, The Empyrean and The Vuil'Raith Cabal.
                      </p>
                      <p class="mb-0">
                        Turning this on ensures at least one asteroid field, one supernova, one nebula and one gravity
                        rift are included in the generated galaxy, whichever tile of that type gets picked. This
                        applies no matter which factions are in the game, unlike Ensure Faction Anomalies.
                      </p>`}
          />
          <HelpModal
            key={"help-balance-planet-traits"}
            visible={this.state.balancePlanetTraitsHelp}
            hideModal={this.toggleBalancePlanetTraitsHelp}
            title={"About Balancing Planet Traits"}
            content={`<p>
                        Normally, the mix of Cultural, Industrial, and Hazardous planets on the board is left entirely
                        to chance, which can occasionally leave one trait so scarce that a trait-based objective
                        becomes nearly unscorable for most players.
                      </p>
                      <p class="mb-0">
                        Turning this on tops up whichever trait is most underrepresented after generation, swapping in
                        additional tiles of that trait so that all three remain reasonably achievable.
                      </p>`}
          />
          <HelpModal
            key={"help-balance-slices"}
            visible={this.state.balanceSlicesHelp}
            hideModal={this.toggleBalanceSlicesHelp}
            title={"About Balancing Slices"}
            content={`<p>
                        Tiles are normally dealt out by how good they are, one to each player in
                        turn, which evens out the <i>ranking</i> of what everybody gets but not its
                        value. Two tiles next to each other in that ranking can be worth wildly
                        different amounts, so it is easy to end up with one player sitting on twice
                        the resources of another.
                      </p>
                      <p>
                        Turning this on measures what each player can actually reach &mdash; the
                        systems within two jumps of their home system, with contested systems split
                        between everyone equally close to them &mdash; and swaps systems between
                        slices until the resources, influence, planets, and anomalies are as even as
                        they can be made.
                      </p>
                      <p class="mb-0">
                        Systems are only ever swapped with others of the same importance to the
                        board, and never in a way that puts two anomalies or two matching wormholes
                        next to each other, so the shape and feel of the board style is kept intact.
                      </p>`}
          />
          <HelpModal
            key={"help-faction-anomalies"}
            visible={this.state.ensureFactionAnomaliesHelp}
            hideModal={this.toggleEnsureFactionAnomaliesHelp}
            title={"About Ensuring Faction Anomalies"}
            content={`<p>
                        Ensures that the corresponding beneficial anomaly is present in the galaxy for each of these
                        factions.
                      </p>
                      <ul class="mb-0">
                        <li><b>Muaat:</b> Supernova</li>
                        <li><b>Saar:</b> Asteroid Field</li>
                        <li><b>Empyrean:</b> Nebula</li>
                        <li><b>Vuil'Raith</b> and <b>Nivyn</b> (DS)<b>:</b> Gravity Rift</li>
                      </ul>`}
          />

          <Button type="submit" className="btn btn-primary">
            Generate
          </Button>
        </Form>
      </div>
    );
  }
}
export default MapOptions;
